import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type { CurrentUser } from './saas/api'
// 编辑器只取类型；组件本体见下面 editorChunk() 那段懒加载。写成 `import type`
// 是必须的 —— 留着值导入，整棵子树又会回到主 chunk，白拆一场。
import type { IntegratedEditorApi } from '@sparks-notation/react-editor/IntegratedEditor'
import { checkQuota, quotaMessage } from './saas/quota'
import { VERSION_VISIBLE_FREE, VERSION_VISIBLE_PLUS, isPlusUser } from './saas/plan'
import type { PlusFeature } from './saas/plan'
import { UpgradeDialog } from './saas/UpgradeDialog'
import * as cloud from './saas/cloud'
import {
	logout,
	displayName,
	onSessionChange,
	confirmPasswordReset,
} from './saas/auth'
import { AuthPage, translateAuthError } from './saas/AuthPage'
import { LibraryPage } from './saas/LibraryPage'
import { AdminPage } from './saas/AdminPage'
import { NoticeModal } from './saas/NoticeModal'
import { ProfilePage } from './saas/ProfilePage'
import { AppDialogHost, appAlert, appConfirm, appPrompt } from './AppDialog'
import { fetchIsAdmin, fetchWatermark } from './saas/admin'
import { useI18n } from './i18n/i18n'
import { NMNI18n, SparksNMN } from '@sparks-notation/core'
// MusicXML 的**导入**（musicXmlToNmn）已移到编曲练习库页面，见 saas/LibraryPage.tsx。
// 这里只留导出（handleMusicXmlDownload → buildStaffXml）。
import { MusicXmlExporter } from '@sparks-notation/react-editor/tone/MusicXmlExporter'
import { WATERMARK_TEXT, drawWatermarkOnCanvas } from '@sparks-notation/react-editor/preview/watermark'
import { controlDataPartDefault } from '@sparks-notation/react-editor/tone/ControlData'
import { useThemedStyles, BREAKPOINT, ThemeContextValue, uiPrimitives, useIsMobile, useEditorColorScheme } from './theme'
// 顶栏是编辑器 / 编曲练习库 / 分享页共用的站点外壳，抽在 PageHeader.tsx
import { PageHeader, HeaderAction } from './PageHeader'
import { HomePage } from './HomePage'
import { takePendingDoc, setPendingDoc } from './pendingDoc'
import { makeDefaultScore } from './defaultScore'
// 曲名的读/写/占位判定集中在一处：库里 `title` 与谱面 `Dt:` 必须是同一个字符串
import { extractTitle, isUntitled, resolveScoreTitle, UNTITLED_SCORE_TITLE, withDocTitle } from './docTitle'
import { findExampleByFile, fetchExampleScore } from './examples'
import { navigateHash } from './navigate'
// 分享到 PC 微信（PC OpenSDK）：只在电脑浏览器里有意义，模块自己会判环境
import { shareToWechatPc } from './saas/wechatPcShare'
import { fetchPitchCalibration } from './saas/profile'
import { useCoreFontsReady } from './coreFonts'
import { CoreFontSplash } from './CoreFontSplash'

// 懒加载：见 TestApp 里 tutorialPage() 的注释。静态 import 会让 12 篇 MDX + KaTeX
// 无条件进主 chunk，首页首屏白白背上几 MB。
const DocsPage = React.lazy(() => import('../docs/DocsPage'))

/**
 * 编辑器整块懒加载。
 *
 * `IntegratedEditor` 拖着 Ace（~950KB 源码）和 react-icons 的 pi / tb / go 三套图标
 * （~8.6MB 源码 —— `react-editor` 里写的是 `import * as Icons from 'react-icons/*'`
 * 这种整包导入，tree-shaking 削不掉），打包后占主 chunk 的一大半；
 * 而指南、首页、编曲练习库、登录页一个编辑器都不用，却每页都得先下它。
 *
 * 为什么是 `React.lazy` 而不是把 EditorPage 整页搬出去：那两个页面
 * （`EditorPage` / `ShareViewPage`）跟本文件里的样式工厂和十几个模块级助手缠在一起，
 * 搬家的 diff 和风险都远大于收益；动态 import 一次同样能把整棵子树切走。
 *
 * `IntegratedEditor` 是 `forwardRef`，`React.lazy` 会把 `ref` 原样透传，`editorRef` 不受影响。
 * 用到的两处（编辑器页、分享页）各套一层 `<React.Suspense>`；因为下面有 preload，
 * 那层 fallback 正常情况下一帧都不会显示。
 */
const editorChunk = () => import('@sparks-notation/react-editor/IntegratedEditor')
const IntegratedEditor = React.lazy(() => editorChunk().then(m => ({default: m.IntegratedEditor})))

/**
 * 谱面底色 —— **刻意不随主题变**。
 *
 * OSMD 与简谱渲染出来的都是黑墨线，深色底上会直接看不见；真曲谱也不存在"夜用黑纸"。
 * 所以这块是白纸，跟 PDF 导出（html2canvas backgroundColor:'#ffffff'）保持一致。
 */
const SHEET_BG = '#ffffff'

/**
 * 历史版本备注的字数上限 —— 与后端 `views_scores.NOTE_MAX_CHARS` 必须一致。
 *
 * 之所以要卡：备注显示在历史版本列表那一行里（标题 · 备注 · 时间 · 回滚），
 * 挤在一行上的宽度是有限的。40 字大约是一句话（"第三段和弦改成 C-Am-F-G"），
 * 再长就只能整行省略号，那时备注就失去了"一眼看出这版改了什么"的作用。
 *
 * 按**码点**数（`Array.from`），不是 `length`：`length` 数的是 UTF-16 码元，
 * 一个 emoji 算 2，会出现"界面显示 30/40 却被后端拒"的错位。
 */
const NOTE_MAX_CHARS = 40

/**
 * 历史版本那一行的时间：`2026-10-29 22:41`。
 *
 * 不用 `toLocaleString()`：它带秒（`2026/10/29 22:41:00`），比这里多出 6 个字符，
 * 而弹窗只有 570px 宽 —— 一多就把版次标题挤成省略号，标题恰恰是这一行要先看清的。
 * 年份保留：跨年回看"去年那版"时，没有年份分不清 10-29 是哪一年。
 */
function fmtVersionTime(iso: string | null): string {
	if (!iso) return '—'
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return '—'
	const p = (n: number) => String(n).padStart(2, '0')
	return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/**
 * 顶栏「文件▾」里是否提供「导出 MusicXML」「导出 HTML」。
 *
 * 产品要求网页版**隐藏**这两项（曲谱主要在站内流通，导出去的裸文件容易被
 * 当成"官方交换格式"拿去二次分发，出了问题无从追溯；站内已有 PDF 导出够用）。
 *
 * 实现刻意**保留**、只把入口藏起来：这是可回退的产品取舍，不是技术决定的
 * 删代码。要恢复就把这里改成 `true` —— 别去 git 历史里翻那两个 handler
 * 重写一遍（`handleMusicXmlDownload` / `exportHtml` 都还在）。
 *
 * 注：`file: true` 的动作只剩这两项了（导入 MusicXML / 导出 PDF 已各自搬走），
 * 所以常量保持 `false` 时顶栏**不会渲染「文件▾」按钮**；改成 `true` 它会跟着回来。
 */
const SHOW_RAW_EXPORTS = false

/**
 * 全站样式表。走 token，不再就地写死颜色（改配色只改 theme/tokens.ts）。
 *
 * 工厂定义在模块级：`useThemedStyles` 内部按 theme memo，工厂引用必须稳定，
 * 写成内联箭头函数会让依赖比较失去意义。
 */
const stylesFactory = (t: ThemeContextValue) => ({
	// 顶栏按钮与浮层菜单：与 PageHeader 共用同一份定义，别再在这里重写一遍
	...uiPrimitives(t),

	userEmail: {
		fontSize: 12,
		color: t.palette.ink3,
	},

	// ── 正文 ────────────────────────────────────────────────────
	editorWrap: {
		flex: 1,
		minHeight: 0,
		display: 'flex',
		flexDirection: 'column',
		'& > div': {
			flex: 1,
		},
		// 移动端浏览器地址栏收起会引发 100vh 抖动，用可视高度代替
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			minHeight: 'calc(100dvh - 110px)',
		},
	},
	hiddenInput: {
		display: 'none',
	},

	// ── 模态框 ──────────────────────────────────────────────────
	modalBackdrop: {
		position: 'fixed',
		inset: 0,
		background: 'rgba(0,0,0,.4)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1000,
	},
	modal: {
		background: t.palette.paper2,
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.lg,
		boxShadow: t.shadows.lg,
		padding: 24,
		width: 520,
		maxWidth: '90vw',
		maxHeight: '70vh',
		overflowY: 'auto',
		color: t.palette.ink,
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			// 窄屏模态框铺满，留出边距
			width: 'calc(100vw - 24px)',
			maxHeight: '80vh',
			padding: 16,
		},
	},
	modalTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 18,
		fontWeight: 700,
		margin: '0 0 12px',
	},
	modalClose: {
		float: 'right',
		fontFamily: t.fonts.serif,
		fontSize: 13,
		padding: '6px 10px',
		borderRadius: t.radius.sm,
		border: `1px solid ${t.palette.line2}`,
		background: t.palette.paper2,
		color: t.palette.ink2,
		cursor: 'pointer',
	},
	versionItem: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '8px 0',
		fontSize: 13,
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			// 时间戳换行显示，避免挤压标题
			flexWrap: 'wrap',
		},
	},
	// 一条版本 +（可能展开的）备注编辑区共用一条分隔线。
	// 分隔线放这里而不是 versionItem 上：否则编辑区会落到线下方，看起来
	// 像是"属于下一条版本"。
	versionBlock: {
		borderBottom: `1px solid ${t.palette.line}`,
	},
	versionTitle: {
		flex: 1,
		// 没有它，flex 子项会被压缩到内容以下 —— 手机上标题会缩成「手…」两个字，
		// 一屏历史版本全成了省略号，等于没列。
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			// 窄屏：标题独占一行，备注 / 时间 / 回滚换到下一行。
			// 挤在一行里的话，备注标签（最多 132px）+ 时间（~140px）+ 按钮（~60px）
			// 就吃掉整行，标题只剩省略号。
			flex: '1 1 100%',
		},
	},
	// 备注按钮：没写过是虚线"＋备注"，写过是一条实底小标签。
	// 两者都用 maxWidth 卡住并省略 —— 完整的备注在 title 提示里。
	versionNote: {
		flex: '0 1 auto',
		maxWidth: 132,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		fontFamily: t.fonts.serif,
		fontSize: 12,
		lineHeight: 1.5,
		padding: '1px 8px',
		borderRadius: t.radius.sm,
		border: `1px solid ${t.palette.line2}`,
		background: t.palette.paper3,
		color: t.palette.ink2,
		cursor: 'pointer',
	},
	versionNoteEmpty: {
		flex: '0 0 auto',
		fontFamily: t.fonts.serif,
		fontSize: 12,
		lineHeight: 1.5,
		padding: '1px 8px',
		borderRadius: t.radius.sm,
		border: `1px dashed ${t.palette.line2}`,
		background: 'transparent',
		color: t.palette.ink3,
		cursor: 'pointer',
		whiteSpace: 'nowrap',
	},
	// 备注编辑区：整行铺开（不是塞进上面那一行）。
	// 那一行只剩两三格宽，40 字在那种宽度里根本没法看自己打了什么。
	noteEditor: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		padding: '0 0 8px',
		fontSize: 12,
	},
	noteInput: {
		flex: 1,
		minWidth: 0,
		fontFamily: t.fonts.serif,
		fontSize: 12,
		padding: '5px 8px',
		borderRadius: t.radius.sm,
		border: `1px solid ${t.palette.line2}`,
		background: t.palette.paper2,
		color: t.palette.ink,
		outline: 'none',
	},
	// 字数用等宽数字，免得 9/40 → 10/40 时整行抖一下
	noteCount: {
		fontSize: 11,
		color: t.palette.ink3,
		fontVariantNumeric: 'tabular-nums',
		whiteSpace: 'nowrap',
	},
	// 行尾的「当前」：盒子尺寸与 tb + tbOutline 完全对齐（padding/border/lineHeight
	// 照抄、底色与描边也照抄），只把字色换成品牌橘红 —— 切换「当前 / 回滚」时
	// 这一行的宽度不该抖一下。
	//
	// 用 `palette.cinnabar` 而不是写死 #A8443A：那个值取自 logo 主色，也就是调色板里
	// 的朱砂；暗色下同一枚 token 会提亮成 #C4655A，否则 #A8443A 压在 #1d2526 上
	// 对比度只有 3.6:1，13px 的小字会糊。
	versionCurrent: {
		fontFamily: t.fonts.serif,
		fontSize: 13,
		lineHeight: 1,
		padding: '7px 12px',
		borderRadius: t.radius.sm,
		border: `1px solid ${t.palette.line2}`,
		background: t.palette.paper2,
		color: t.palette.cinnabar,
		fontWeight: 500,
		whiteSpace: 'nowrap',
	},
	versionTime: {
		color: t.palette.ink3,
		fontSize: 12,
		whiteSpace: 'nowrap',
	},
	/**
	 * 历史版本底部的额度说明。免费用户只看得到 3 条，不写清楚会以为"我的版本
	 * 被删了"—— 其实没删，升级就能看到（见 saas/plan.ts 的 versionLimitOf）。
	 */
	versionHint: {
		marginTop: 12,
		paddingTop: 10,
		borderTop: `1px dashed ${t.palette.line}`,
		fontSize: 12,
		lineHeight: 1.6,
		color: t.palette.ink3,
	},
	versionHintUp: {
		marginLeft: 6,
		border: 'none',
		background: 'none',
		padding: 0,
		font: 'inherit',
		color: t.palette.cinnabar,
		cursor: 'pointer',
		textDecoration: 'underline',
	},
	shareLinkRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		padding: '6px 0',
		fontSize: 12,
	},
	shareUrl: {
		flex: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		background: t.palette.paper3,
		borderRadius: t.radius.sm,
		padding: '5px 8px',
		userSelect: 'all',
		fontFamily: t.fonts.mono,
	},
	empty: {
		fontSize: 13,
		color: t.palette.ink3,
		textAlign: 'center',
		padding: '20px 0',
	},
	// 「分享到微信」那一排动作 + 它下面那行说明
	shareActions: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		flexWrap: 'wrap',
	},
	shareHint: {
		margin: '8px 0 0',
		fontSize: 12,
		lineHeight: 1.6,
		color: t.palette.ink3,
	},
	shareNotice: {
		margin: '8px 0 0',
		fontSize: 12,
		// 青瓷墨 —— 与站内"链接/激活"同一个色，不用朱砂（那是主按钮的限量色）
		color: t.palette.celadonInk,
	},

	// ── 五线谱预览 ──────────────────────────────────────────────
	staffSheet: {
		width: '100%',
		minHeight: 300,
		background: SHEET_BG,
		borderRadius: t.radius.md,
		padding: 12,
		boxSizing: 'border-box',
	},
	staffBar: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		marginBottom: 8,
	},
	staffHint: {
		fontSize: 12,
		color: t.palette.ink3,
	},

	// ── 整页态（加载中 / 居中卡片）──────────────────────────────
	loading: {
		minHeight: '100vh',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		background: t.palette.paper,
		color: t.palette.ink,
		fontFamily: t.fonts.sans,
		fontSize: 15,
		padding: 16,
		boxSizing: 'border-box',
	},
	// 编辑器 chunk 的 Suspense 兜底。**不能复用上面的 `loading`** —— 那个是 `minHeight: 100vh`
	// 的整页态，塞进 editorWrap 里会把版面撑开。这里只是个占位的居中一行字，
	// 高度由 editorWrap 的 `& > div { flex: 1 }` 决定。
	editorLoading: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: t.palette.ink3,
		fontFamily: t.fonts.sans,
		fontSize: 14,
	},
	// 找回密码页的居中卡片
	card: {
		background: t.palette.paper2,
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.lg,
		boxShadow: t.shadows.sm,
		padding: 32,
		width: 'min(380px, 100%)',
		boxSizing: 'border-box',
	},
	cardTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 18,
		fontWeight: 700,
		marginBottom: 4,
	},
	cardSub: {
		fontSize: 13,
		color: t.palette.ink3,
		marginBottom: 20,
	},
	formError: {
		// 8 位十六进制（末尾 1f = 12% alpha）而不是 color-mix()：
		// 后者在国内常见的旧版微信/安卓内置浏览器上还不支持，一失效就是透明底红字看不清
		background: t.palette.cinnabar + '1f',
		color: t.palette.cinnabar,
		borderRadius: t.radius.sm,
		padding: '8px 12px',
		fontSize: 13,
		marginBottom: 12,
	},
	input: {
		width: '100%',
		fontSize: 15,
		fontFamily: t.fonts.sans,
		padding: '10px 12px',
		border: `1px solid ${t.palette.line2}`,
		borderRadius: t.radius.sm,
		background: t.palette.paper,
		color: t.palette.ink,
		outline: 'none',
		boxSizing: 'border-box',
		'&:focus': {
			borderColor: t.palette.celadon,
		},
	},
})

const useStyles = () => useThemedStyles(stylesFactory)

// 从 NMN 源码提取 qpm（供五线谱跟谱播放定速）
function extractQpm(content: string): number {
	const m = content.match(/qpm\s*=\s*(\d+(?:\.\d+)?)/)
	return m ? Math.max(30, Math.round(parseFloat(m[1]))) : 90
}

// 五线谱渲染 + 跟谱播放（OSMD Cursor 按音符边界步进，Tone.js 同步发声）
function OsmdViewer(props: { xmlText: string, qpm?: number, onContainerReady?: (el: HTMLDivElement | null) => void }) {
	const classes = useStyles()
	const isMobile = useIsMobile()
	const containerRef = useRef<HTMLDivElement | null>(null)
	const osmdRef = useRef<any>(null)
	const synthRef = useRef<any>(null)
	const timerRef = useRef<number | null>(null)
	const stoppedRef = useRef(true)
	const [ready, setReady] = useState(false)
	const [playing, setPlaying] = useState(false)

	useEffect(() => {
		if (!containerRef.current || !props.xmlText) return
		let active = true
		setReady(false)
		// 提示文案走 CSS 变量：GlobalStyle 把 token 落到了 :root，内嵌 HTML 也取得到
		containerRef.current.innerHTML = '<div style="padding: 24px; text-align: center; color: var(--ink-3);">正在渲染五线谱…</div>'
		import('opensheetmusicdisplay').then(({ OpenSheetMusicDisplay }) => {
			if (!active || !containerRef.current) return
			containerRef.current.innerHTML = ''
			const osmd = new OpenSheetMusicDisplay(containerRef.current, {
				autoResize: true,
				drawTitle: true,
				drawingParameters: 'compact',
				followCursor: true,
			})
			osmd.load(props.xmlText).then(() => {
				if (!active) return
				osmd.render()
				// 移动端：OSMD 的 autoResize 只重排系统行、不缩放字号，compact 档在
				// ~360px 小屏上既偏大又可能横向溢出（音符/歌词挤到页面外）。这里
				// ① 先把字号整体再缩小一档（0.8 倍），② 若仍溢出则等比再缩到贴满
				// 容器。桌面端不动（compact 0.7 在宽屏本就合适）。
				if (isMobile) {
					try {
						const el = containerRef.current
						if (el) {
							const svg = el.querySelector('svg')
							const natural = svg ? svg.getBoundingClientRect().width : el.scrollWidth
							const avail = el.clientWidth
							let zoom = 0.8
							if (natural > avail + 1) {
								zoom = Math.min(zoom, Math.max(0.3, avail / natural))
							}
							osmd.Zoom = zoom
							osmd.render()
						}
					} catch {}
				}
				try { osmd.enableOrDisableCursors(true) } catch {}
				osmdRef.current = osmd
				setReady(true)
			}).catch(err => {
				if (active && containerRef.current) {
					containerRef.current.innerHTML = `<div style="padding: 24px; color: var(--cinnabar);">五线谱渲染失败：${err?.message || err}</div>`
				}
			})
		}).catch(err => {
			if (active && containerRef.current) {
				containerRef.current.innerHTML = `<div style="padding: 24px; color: var(--cinnabar);">加载 OSMD 失败：${err?.message || err}</div>`
			}
		})
		return () => {
			active = false
		}
	}, [props.xmlText, isMobile])

	function stopPlayback() {
		stoppedRef.current = true
		if (timerRef.current != null) {
			window.clearTimeout(timerRef.current)
			timerRef.current = null
		}
		try { osmdRef.current?.cursor?.hide() } catch {}
		try { synthRef.current?.releaseAll?.() } catch {}
		setPlaying(false)
	}

	useEffect(() => {
		return () => {
			// 卸载时停播放、释放合成器
			stoppedRef.current = true
			if (timerRef.current != null) window.clearTimeout(timerRef.current)
			try { synthRef.current?.dispose?.() } catch {}
		}
	}, [])

	async function togglePlay() {
		if (playing) {
			stopPlayback()
			return
		}
		const osmd = osmdRef.current
		if (!osmd?.cursor) return
		try {
			if (!synthRef.current) {
				const Tone = await import('tone')
				await Tone.start()
				const synth = new Tone.PolySynth(Tone.Synth).toDestination()
				synth.volume.value = -8
				synthRef.current = synth
			}
		} catch (err: any) {
			void appAlert({title: '音频初始化失败', message: String(err?.message ?? err), confirmText: '知道了'})
			return
		}
		stoppedRef.current = false
		setPlaying(true)
		const cursor = osmd.cursor
		const beatSeconds = 60 / Math.max(30, props.qpm ?? 90)
		try { cursor.reset() } catch {}
		try { cursor.show() } catch {}
		let prevTs = 0

		const step = () => {
			if (stoppedRef.current) return
			const it = cursor.iterator
			if (!it || (it as any).EndReached) {
				stopPlayback()
				return
			}
			// 当前光标位置的所有音符发声（currentVoiceEntries 是 TS private，运行时可读但不可调用）
			try {
				const entries: any[] = (it as any).currentVoiceEntries ?? []
				const freqs: number[] = []
				let longestQuarter = 0.12
				for (const entry of entries) {
					for (const note of (entry.Notes ?? [])) {
						if (note.isRest()) continue
						const f = note.Pitch?.Frequency
						if (typeof f == 'number' && f > 0) {
							freqs.push(f)
							// 音符实值时长（拍）→ 秒，延音线尾音按 Length 全长响
							const len = note.Length?.RealValue
							if (typeof len == 'number' && len > longestQuarter) {
								longestQuarter = len
							}
						}
					}
				}
				if (freqs.length > 0) {
					synthRef.current.triggerAttackRelease(freqs, Math.max(0.1, longestQuarter * beatSeconds * 0.95))
				}
			} catch (err) {
				console.warn('跟谱播放取音符失败', err)
			}
			// 推进光标，按两位置的时间戳差安排下一次步进
			try { cursor.next() } catch {}
			let waitMs = 300
			try {
				const ts2 = cursor.iterator?.currentTimeStamp?.RealValue
				if (typeof ts2 == 'number') {
					waitMs = Math.max(40, (ts2 - prevTs) * beatSeconds * 1000)
					prevTs = ts2
				}
			} catch {}
			timerRef.current = window.setTimeout(step, waitMs)
		}
		step()
	}

	// 跟谱播放工具条暂时隐藏（用户要求）；播放逻辑保留，恢复时把 showPlayback 打开即可
	const showPlayback = false
	return <div style={{ width: '100%' }}>
		{showPlayback && <div className={classes.staffBar}>
			<button className={playing ? classes.tb : classes.tbPrimary}
				disabled={!ready}
				onClick={() => togglePlay()}>
				{playing ? '■ 停止' : '▶ 跟谱播放'}
			</button>
			<span className={classes.staffHint}>
				{ready ? '光标随节拍移动并同步发声（试听音色为预览合成器）' : '五线谱加载中…'}
			</span>
		</div>}
		{/* 谱面恒白：见 SHEET_BG 注释 */}
		<div ref={el => {
			containerRef.current = el
			props.onContainerReady?.(el)
		}} className={classes.staffSheet} />
	</div>
}

function getQueryVariable(variable: string) {
	var query = window.location.search.substring(1);
	var vars = query.split("&");
	for (var i = 0; i < vars.length; i++) {
		var pair = vars[i].split("=");
		if (pair[0] == variable) { return decodeURIComponent(pair[1]); }
	}
	return undefined
}

// 安全调 ref：编辑器 API 就绪时执行
function callRef<T>(ref: {current: T | null}, fn: (val: T) => void) {
	if(ref.current) {
		fn(ref.current)
	}
}

/**
 * 编辑器 ref + 「它到底挂上了没有」。
 *
 * 编辑器现在是懒加载的（见上面 `editorChunk()`），**首帧必然不在**；而 `callRef` 遇到
 * 没挂载的 ref 是**静默空操作**。所以「挂载时灌初始内容」这类 effect 不能只依赖
 * `props.scoreId` / `state` —— 那一枪打在空处，内容被悄悄丢掉且**不会再补**
 * （编辑器页会退成空白文档，分享页会退成空编辑器）。
 *
 * 回调 ref 而不是「effect 里读 `editorRef.current`」：只有前者能在挂上的那一刻
 * 触发重渲染，让那些 effect 有机会补上第二枪。
 *
 * `attached` 是**单向闩**（只翻一次，不会翻回去）：万一编辑器在页面还活着的时候
 * 被卸载重挂，那几个 effect 会跟着再跑一遍，把用户没保存的改动冲掉。
 */
function useEditorRef() {
	const ref = useRef<IntegratedEditorApi | null>(null)
	const [attached, setAttached] = useState(false)
	const attach = useCallback((api: IntegratedEditorApi | null) => {
		ref.current = api
		if(api != null) setAttached(true)
	}, [])
	return [ref, attach, attached] as const
}

function downloadBlob(blob: Blob, filename: string) {
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = filename
	a.click()
	URL.revokeObjectURL(url)
}

// 历史版本的裁剪（只留最近 20 条）已挪到后端：score update 接口在
// 留档旧版本的**同一个事务里**顺手裁剪。前端单独调一次裁剪接口会在
// 并发保存时把两份内容都留下（谁都以为自己是最新的那条）。
// 见 backend/core/views_scores.py 的 _prune_versions。

let exportTemplateCache: string | undefined
async function getExportTemplate(): Promise<string> {
	if(exportTemplateCache != null) return exportTemplateCache
	// 模板在 public/static/export-template.txt（与桌面版同源，desktop/presync.ts 生成拷贝）。
	// 之前写的 ./export-template.html 不存在，SPA fallback 会把 index.html 当模板喂回来 —— 静默产出打不开的导出文件。
	const resp = await fetch('./static/export-template.txt')
	if(!resp.ok) {
		throw new Error(`导出模板读取失败（${resp.status}）`)
	}
	const text = await resp.text()
	// SPA fallback 的特征：拿到的是本应用 index.html 而不是模板（部署没拷 static/ 目录时仍会发生）
	if(text.includes('<div id="root">') && !text.includes('score-partwise') && text.includes('<script type="module"')) {
		throw new Error('导出模板缺失：部署时需要带上 static/export-template.txt')
	}
	exportTemplateCache = text
	return exportTemplateCache
}

// 新谱的初始内容在 defaultScore.ts —— 与「＋ 新建曲谱」共用同一份，改一处就够

// SaaS hash 路由：
//   #        首页（未匹配的一律回这里）| #/login 登录注册 | #/library 编曲练习库
//   #/edit   编辑器（本地暂存）| #/edit/<id> 编辑云端曲谱 | #/share/<id> 只读分享 | #/reset 找回密码
//   #/tutorial 指南（引言）| #/tutorial/<slug> 指南某一篇
//
// ⚠️ 整串 hash 就是路由，**没有第二个参数位** —— 所以指南正文里的标题锚点
//    （`#shift` 这种）绝不能往 hash 里塞，否则会当场被解析成"未知页面"弹回首页。
//    锚点一律走 `scrollIntoView`，见 docs/DocsPage.tsx。
type Route =
	| {page: 'home'}
	| {page: 'login'}
	| {page: 'library'}
	| {page: 'profile'}
	| {page: 'edit', scoreId?: string}
	| {page: 'share', shareId: string}
	| {page: 'reset'}
	| {page: 'tutorial', slug?: string}
	| {page: 'admin'}

// 保存成功提示的跨重挂载转交：首次上云 / 另存副本都会 hashchange 重建编辑器，
// 直接 setSaveState 会被重挂载清零 —— 用户看到的正是"点了保存什么提示都没有"。
// 与 pendingDoc 同款的一次性变量：写入 → 重挂载时取走显示 → 清空。
let pendingSaveNotice: string | null = null

// 认证回跳意图是否已被 parseHash 消费（页面初次载入消费一次，后续 hashchange 不再消费）
let pendingAuthRouteConsumed = false

function parseHash(): Route {
	const hash = location.hash.replace(/^#/, '')
	if(hash === 'library') return {page: 'library'}
	if(hash === 'profile') return {page: 'profile'}
	if(hash === 'admin') return {page: 'admin'}
	if(hash === 'login') return {page: 'login'}
	if(hash === 'reset') return {page: 'reset'}
	// 认证回跳（找回密码/邮箱确认）：GoTrue 验证完会把会话参数塞进 URL 片段，
	// 顶掉 redirect_to 里原本的 #reset —— 在这里接住。supabase-js 的
	// detectSessionInUrl 会消费 token 并清掉片段，本页据此显示重设表单或无效提示
	if(hash.startsWith('access_token=') || hash.startsWith('error=') || hash.startsWith('reset#access_token=')) return {page: 'reset'}
	const tm = hash.match(/^tutorial\/(.+)$/)
	if(tm) return {page: 'tutorial', slug: decodeURIComponent(tm[1])}
	if(hash === 'tutorial') return {page: 'tutorial'}
	const sm = hash.match(/^share\/([0-9a-fA-F-]+)$/)
	if(sm) return {page: 'share', shareId: sm[1]}
	const m = hash.match(/^edit\/(.+)$/)
	if(m) return {page: 'edit', scoreId: m[1]}
// 必须显式写：以前 `#edit` 是靠下面那句兜底才落到编辑器的，现在兜底归首页。
		// 编曲练习库顶栏的「用编辑器打开」按钮已移除（新建曲谱本来就会跳进编辑器），
		// 这条路由现在只服务手输地址/书签的直达入口，但仍然要留着。
		if(hash === 'edit') return {page: 'edit'}
	// 认证回跳的意图兜底：supabase-js 可能抢在 React 解析路由之前把 URL 片段消费掉
	//（清空后上面的 access_token 分支匹配不上，用户被甩到首页——竞态，两次点击结果不同
	// 就是它）。归一化时暂存到 sessionStorage 的意图路由在这里取出。
	// ⚠️ 只消费一次（首帧），后续 hashchange 不再消费，避免把正常导航劫持到重设页。
	if(!pendingAuthRouteConsumed) {
		pendingAuthRouteConsumed = true
		try {
			const pending = sessionStorage.getItem('pending-auth-route')
			if(pending) {
				sessionStorage.removeItem('pending-auth-route')
				return {page: pending} as Route
			}
		} catch {}
	}
	return {page: 'home'}
}

function EditorPage(props: {session: CurrentUser | null, scoreId?: string, onLogout: () => void}) {
	const [editorRef, attachEditor, editorAttached] = useEditorRef()
	const LNG = useI18n()
	const classes = useStyles()
	const isMobile = useIsMobile()
	// 编辑器内部那套色槽，由全站 token 派生。见 theme/editorColorScheme.ts
	const editorColors = useEditorColorScheme()
	const scoreIdRef = useRef<string | undefined>(props.scoreId)
	const [cloudScoreId, setCloudScoreId] = useState<string | undefined>(props.scoreId)
	const [saveState, setSaveState] = useState<string>('')
	const [userEmail, setUserEmail] = useState('')
	const loggedIn = !!props.session
	const [historyOpen, setHistoryOpen] = useState(false)
	// created_at 后端可能返回 null（字段存在但无值），类型跟着放宽。
	// note 是用户自己写的版本备注，没写过时是空串（不是 null）。
	// is_active 由后端算（回滚过 → 那一版；没回滚 → 最新那条），前端只负责显示 ——
	// "该标哪一行"是数据规则，两处各写一套迟早会不一致。
	const [historyList, setHistoryList] = useState<{id: string, title: string, created_at: string | null, note: string, is_active: boolean}[] | null>(null)
	// 正在编辑哪一条备注（version id），null = 没有在编辑。
	// 同时只编辑一条：备注很短，弹两个输入框只会让人不知道焦点在哪。
	const [noteEditingId, setNoteEditingId] = useState<string | null>(null)
	const [noteDraft, setNoteDraft] = useState('')
	const [noteSaving, setNoteSaving] = useState(false)
	// MusicXML 导入已移到编曲练习库页面（只对管理员显示，见 saas/LibraryPage.tsx），
	// 编辑页不再保留隐藏的 file input。
	// 分享链接
	const [shareOpen, setShareOpen] = useState(false)
	// created_at / expires_at 后端可能返回 null（字段存在但无值），
	// 所以类型跟着放宽 —— 用空串兜底会造出一个看起来合法的假时间。
	const [shareLinks, setShareLinks] = useState<{id: string, url: string, created_at: string | null, expires_at?: string | null}[]>([])
	const [shareGenerating, setShareGenerating] = useState(false)
	// 「分享到微信」的忙碌态 / 成功提示。忙碌态同时兼作防连点：微信侧
	// 按用户限流 1 次/秒、5 次/分，连点只会拿到 errcode 8。
	const [shareWxBusy, setShareWxBusy] = useState(false)
	const [shareWxNotice, setShareWxNotice] = useState('')
	const [currentXmlTitle, setCurrentXmlTitle] = useState('')
	// 当前打开的曲谱的归属。
	//
	// ⚠️ 用 `isMine: boolean` 而不是原来的 `ownerId: string|null`：
	//   所有权判定是服务端的事实（RLS 已经保证了"别人的谱你根本读不到"，
	//   能读到就基本是自己的），由后端在读曲谱时一并返回 is_owner。
	//   前端拿两个 id 互相比对，一是多此一举，二是以后若支持"协作者"
	//   之类的从属关系，这套比对会静默判错。
	//
	// `creator` 仍要保留：它区分「首页样张（署名 manmantan）」与用户自己的谱 ——
	// 样张还没有云端行（isMine 为 false 靠 creator 判定）。
	const [openedMeta, setOpenedMeta] = useState<{isMine: boolean, creator: string | null}>(
		{isMine: true, creator: null},
	)
	// saveToCloud 被 45 秒自动保存的 setInterval 长期持有（deps 只有 loggedIn），
	// 直接读 state 会拿到过期闭包 —— 归属与登录态一律走 ref 取最新值。
	const openedMetaRef = useRef(openedMeta)
	openedMetaRef.current = openedMeta
	const sessionRef = useRef(props.session)
	sessionRef.current = props.session

	// 五线谱预览 Modal
	const [staffOpen, setStaffOpen] = useState(false)
	const [staffXml, setStaffXml] = useState('')
	const [staffQpm, setStaffQpm] = useState(90)
	// OSMD 渲染容器的 DOM（由 OsmdViewer 回传），PDF 导出直接截这个元素
	const staffContainerRef = useRef<HTMLDivElement | null>(null)

	// 曲谱水印文案：初值用组件默认常量（编辑器预览立即可见），挂载后读后台
	// site_settings 覆盖。读失败则保持默认（含离线 / 表未建）。各导出出口与
	// 预览都从这里取，管理员在后台改的是同一份。
	//
	// ⚠️ 这是**站点配置**，不是"当前用户该看到的水印"。会员差异在下游一处统一处理
	// （见下面的 `watermark`）：Plus 会员导出与预览都不带水印。两个概念别混，
	// 否则后台改配置时会莫名其妙地"只有免费用户看到新文案"。
	const [ siteWatermark, setSiteWatermark ] = useState(WATERMARK_TEXT)
	useEffect(() => {
		let cancelled = false
		fetchWatermark().then(text => {
			if(!cancelled) setSiteWatermark(text)
		})
		return () => { cancelled = true }
	}, [])

	/**
	 * 会员水印：Plus 会员为空串（预览、PDF、五线谱 PDF 三处出口都读它）。
	 *
	 * 之所以在**这个变量上**做一次性处理而不是逐个出口判断：水印有四个出口
	 * （预览叠层 / 编辑页 PDF / 五线谱 PDF / 导出 HTML），每个地方各写一遍
	 * `isPlus ? '' : watermark`，迟早会漏掉一个 —— 漏掉的那个就是"会员导出
	 * 还有水印"的投诉来源。空串已经是所有出口的既定语义（PreviewView 会跳过
	 * 渲染，drawWatermarkOnCanvas 直接 return）。
	 */
	const isPlus = isPlusUser(props.session)
	const watermark = isPlus ? '' : siteWatermark

	// 升级弹窗。菜单里被 Plus 锁挡住的三个入口（跟弹 / 自动左手伴奏 / 更多）
	// 点了就落到这里 —— 组件库只负责"拦住 + 告诉我点的是哪个"，弹什么由本站决定。
	// `useCallback` 让它保持稳定引用：editorPrefs 是 memo 的，每次给新函数
	// 等于每次重建 contextValue。
	const [ upgrade, setUpgrade ] = useState<{open: boolean, feature?: PlusFeature}>({open: false})
	const openUpgrade = useCallback((feature: PlusFeature) => {
		setUpgrade({open: true, feature})
	}, [])

	useEffect(() => {
		setUserEmail(props.session?.email ?? '')
	}, [])

	// 消费跨重挂载的保存成功提示（首存/另存副本触发过 hashchange 重建编辑器，
	// 旧实例上的 setSaveState 已被清零）。取走即清，避免每次回编辑器都重放。
	useEffect(() => {
		if (pendingSaveNotice) {
			setSaveState(pendingSaveNotice)
			pendingSaveNotice = null
		}
	}, [])

	useEffect(() => {
		// 编辑器还没挂上就先别动 —— 这一枪打在空处，内容会被悄悄丢掉且不会再补。
		// `editorAttached` 翻转会让这个 effect 重跑，那时 `takePendingDoc()` 才真正消费。
		// 所以这一行必须在 `takePendingDoc()` **之前**。
		if (!editorAttached) return
		if (!props.scoreId) {
			// 本地暂存模式。三级优先：首页点进来的曲谱 → 浏览器里的草稿 → 一份新谱。
			// pending 在这里取而不是在下面的回调里取：callRef 遇到编辑器尚未挂载是**静默空操作**，
			// 放进回调就成了"没挂上就悄悄丢掉、还白白留着待消费状态"。
			const pending = takePendingDoc()
			// 首页样张带着创建者署名（manmantan）——保存时按它另存副本；草稿/新谱归自己
			setOpenedMeta({isMine: true, creator: pending?.creator ?? null})
			// 顶栏曲名也得跟着 pending 走。这条路径原本只把标题塞进编辑器实例，
			// `currentXmlTitle` 一直空着 → 顶栏永远显示「未命名曲谱」，而曲子本身
			// 明明有名字（曲库 391 首点进来全是这样）。草稿/新谱仍然留空。
			setCurrentXmlTitle(pending?.title ?? '')
			callRef(editorRef, api => {
				if (pending) {
					api.triggerOpen({path: pending.title, content: pending.content})
					return
				}
				const storedData = localStorage.getItem('sparks-nmn-demo-src')
				const data = storedData != null ? storedData : makeDefaultScore()
				api.triggerOpen({path: '', content: data})
			})
			return
		}
		// 云端曲谱加载
		(async () => {
			// 注意 404 有两种含义：不���在，或**不属于你**（RLS 会挡住）。
			// 后端有意不区分——返回 403 会泄露"这个 id 确实存在"。
			// 所以文案只说"不存在"，不提示"无权限"（那等于确认它存在）。
			// scoreId 在上面已 if (!props.scoreId) return 过，但 TS 不会
			// 把那个提前 return 记到闭包里，这里显式收窄类型。
			const scoreId = props.scoreId
			if (!scoreId) return
			let data: cloud.CloudScoreDetail
			try {
				data = await cloud.loadScore(scoreId)
			} catch (err: any) {
				await appAlert({
					title: '曲谱加载失败',
					message: err?.message ?? '该曲谱不存在',
					confirmText: '知道了',
				})
				navigateHash('#library')
				return
			}
			scoreIdRef.current = data.id
			setCloudScoreId(data.id)
			setCurrentXmlTitle(data.title)
			setOpenedMeta({isMine: data.is_owner, creator: data.creator ?? null})
			callRef(editorRef, api => {
				api.triggerOpen({path: data.title, content: data.content})
			})
		})()
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.scoreId, editorAttached])

	async function saveToCloud(options?: {silent?: boolean}) {
		const silent = options?.silent ?? false
		callRef(editorRef, async api => {
			api.triggerBeforeSave()
			const content = api.getValue()
			const path = api.getFilename()
			/**
			 * 曲名的三个来源，优先级：**谱面 `Dt:` → 库名/文件名 → 占位名**。
			 *
			 * 谱面优先：那一行是作者亲手敲的。文件名只是"内容从哪来的"，从
			 * 「＋ 新建曲谱」进来再导入一份带 `Dt:` 的谱时，它是占位名或导入时的
			 * 标题 —— 按旧顺序（文件名在前）就会把用户刚写的 `Dt: love story`
			 * 丢掉（用户实测报的就是这条）。判据与来由都写在 `resolveScoreTitle`。
			 *
			 * 「未命名曲谱」不算名字：那是新建时为了能建行先占的值，把它当真名
			 * 的话，用户点保存只会原样写回，取名框永远不会弹。
			 */
			const rawPath = typeof path === 'string' ? path : ''
			const {title: resolvedTitle, source: titleSource} = resolveScoreTitle({content, path: rawPath})
			let title = resolvedTitle
			// 归属与登录态走 ref：自动保存的 setInterval 长期持有老闭包，读 state 会拿到过期值
			const session = sessionRef.current
			const meta = openedMetaRef.current
			// 未登录：无论有无改动，点保存一律引导登录。草稿先落本机（登录后编曲练习库
			// 有一键迁移，内容不丢）—— 不做会变成"点了保存毫无反应"（用户实测）。
			// ⚠️ 必须拦在 scoreIdRef 分支**之前**：登出后 scoreIdRef 还留着，往下走会
			// 在 session 为 null 上解引用（历史上真炸过 "Cannot read properties of null"）。
			if (!session) {
				localStorage.setItem('sparks-nmn-demo-src', content)
				api.triggerSaved('')
				const go = await appConfirm({
					title: '保存需要登录',
					message: '草稿已暂存到本机浏览器。保存到云端编曲练习库需要先登录，现在前往登录吗？\n（登录后可在编曲练习库把这份草稿一键收进来）',
					confirmText: '前往登录',
					cancelText: '留在本页',
				})
				if (go) navigateHash('#login')
				return
			}
			// 「另存副本」意图：打开的是别人的谱（后端说is_owner=false）或首页样张
			//（尚无云端行，署名非本人）。
			// ⚠️ 副本**不看 isDirty** —— 一字未改也是一份值得入库的谱（用户实测指出的 bug）。
			const isCopy = (scoreIdRef.current && !meta.isMine)
				|| (!scoreIdRef.current && !!meta.creator && meta.creator !== displayName(session))
			/**
			 * 还没有云端身份 = 新谱。
			 *
			 * ⚠️ 新谱**不看 isDirty**：它一字未改也值得入库（默认样张 / 刚导入的
			 * MusicXML 都属于这一种）。按 isDirty 拦的话，用户点保存只会看到
			 * 「内容未改动」，永远存不进编曲练习库（用户实测报的就是这条）。
			 */
			const isNewScore = !scoreIdRef.current
			/**
			 * 名字还没定 = 谱面没写 `Dt:`，而且库里只有占位名（或者根本没有库名）。
			 *
			 * 新建的起手模板刻意不带 `Dt:`（`defaultScore.ts`），所以「新建 → 直接
			 * 点保存」的谱是真的没名字。这种谱**不能按 isDirty 判「无需保存」**：
			 * 用户什么都没改，但名字这件正事还没做，一拦就变成"点了保存毫无反应"，
			 * 名字也永远定不下来。
			 */
			const needsTitle = titleSource === 'placeholder'
			if (!isCopy && !isNewScore && !needsTitle && !api.getIsDirty()) {
				// 有反馈的"无需保存"，别静默返回 —— 用户会以为保存坏了（用户实测）
				if (!silent) setSaveState('内容未改动，无需保存')
				return
			}
			/**
			 * **只在没有曲名时**才问名字。
			 *
			 * 谱面的 `Dt:` 就是作者亲手写的曲名 —— 它比任何弹框都可信，
			 * 有就直接用（再问一遍等于让人把刚打的字重打一遍）；改了名字也只用
			 * 改这一行，保存时自然带上。
			 * 没写才弹框：库里建一行必须有名字，而新谱可以是真的没名字。
			 *
			 * 覆盖的场景不只是"新谱"：从「＋ 新建曲谱」进来的占位谱也有云端 id
			 *（有 id 才能刷新恢复），第一次保存同样是"还没名字"。
			 *
			 * 静默保存（45 秒自动保存）不弹框 —— 弹窗会打断正在打字的人。
			 */
			let contentToSave = content
			if (!isCopy && !silent && needsTitle) {
				const named = await appPrompt({
					title: '给这份谱子起个名字',
					message: '这份谱子还没有曲名。名字会写进谱面的「Dt:」一行，也是编曲练习库里的名字。',
					defaultValue: '',
					placeholder: '例如：小星星（柱式和弦）',
					confirmText: '保存',
					validate: v => v.trim() ? (v.trim().length > 60 ? '名字太长了（最多 60 字）' : null) : '名字不能为空',
				})
				if (named === null) {
					// 取消 = 这次不保存。别把状态留在「保存中…」
					setSaveState('')
					return
				}
				title = named.trim()
				contentToSave = withDocTitle(content, title)
			} else if (!silent && titleSource === 'path') {
				/**
				 * 名字来自文件名而不是谱面：谱面根本没有 `Dt:`（有的话优先就是它了）。
				 * 把它补进来，让库里那一列与谱面印的名字是同一个字符串 ——
				 * `docTitle.ts` 的文件头就是这条约定，否则下次打开会看到
				 * 「库里叫 A、谱面上没名字」。
				 *
				 * 静默保存（45 秒自动）不补：那会在用户没动手的时候改他的源码。
				 */
				contentToSave = withDocTitle(content, title)
			}
			if (!silent) setSaveState('保存中…')
			try {
				if (isCopy) {
					// 打开的是别人的曲谱：不覆盖原件，另存一份副本到自己库。
					// 副本继承创建者署名（出处），所有者是自己 —— 之后按「自己的谱」同步。
					const quota = await checkQuota('create_score')
					if (!quota.allowed) {
						setSaveState('')
						void appAlert({title: '配额不足', message: quotaMessage('create_score', quota), confirmText: '知道了'})
						return
					}
					// 查重：库里已有同名曲谱时，让用户选「覆盖」或「打开已有」，别静默多一份
					//（用户实测场景：库里存过《小星星》，再从首页打开小星星样张又另存，会攒出重名副本）
					const dup = await cloud.findByTitle(title)
					if (dup) {
						const overwrite = await appConfirm({
							title: '编曲练习库已有同名曲谱',
							message: '编曲练习库已存在同名曲谱《' + title + '》，保存会覆盖它。\n覆盖前的旧版本会自动留档，可在「历史版本」找回。',
							confirmText: '覆盖保存',
							cancelText: '先不覆盖',
							danger: true,
						})
						if (overwrite) {
							// 覆盖：留档旧版本再更新。后端在**同一事务**里做这两件事 ——
							// 原来在前端分两次调用，中间失败会留下"被改过但没留档"的谱。
							await cloud.updateScore(dup.id, title, contentToSave)
							scoreIdRef.current = dup.id
							setCloudScoreId(dup.id)
							pendingSaveNotice = '已覆盖编曲练习库中的《' + title + '》' + new Date().toLocaleTimeString()
							history.replaceState(undefined, '', '#edit/' + dup.id)
							window.dispatchEvent(new HashChangeEvent('hashchange'))
						} else if (await appConfirm({
							title: '打开已有曲谱',
							message: '要打开编曲练习库中的《' + title + '》吗？\n当前对曲谱的编辑将不会保留。',
							confirmText: '打开已有曲谱',
							cancelText: '取消',
						})) {
							// 打开库里已有的那份（当前对样张的编辑随之放弃 —— 用户两次确认过）
							scoreIdRef.current = dup.id
							setCloudScoreId(dup.id)
							history.replaceState(undefined, '', '#edit/' + dup.id)
							window.dispatchEvent(new HashChangeEvent('hashchange'))
							return
						} else {
							return
						}
					} else {
						const newId = await cloud.saveNewScore(title, contentToSave)
						scoreIdRef.current = newId
						setCloudScoreId(newId)
						pendingSaveNotice = '已另存到你的编曲练习库 ' + new Date().toLocaleTimeString()
						history.replaceState(undefined, '', '#edit/' + newId)
						window.dispatchEvent(new HashChangeEvent('hashchange'))
					}
				} else if (scoreIdRef.current) {
					// 自己的谱：留档旧版本 + 更新由后端在同一事务内完成。
					// 前端不再先读旧内容再插版本 —— 那样两次往返之间有窗口期，
					// 并发保存（比如自动保存撞上手动保存）会丢版本。
					await cloud.updateScore(scoreIdRef.current, title, contentToSave)
				} else {
					// 新曲上云受配额约束（更新已有曲不受限）。能走到这里 creator 必是本人。
					// creator 不再由前端传 —— 后端从 Profile.nickname 自动填
					//（core/signals.py 的 fill_score_creator），前端传的值还可能
					// 覆盖掉别人更新的昵称。
					const quota = await checkQuota('create_score')
					if (!quota.allowed) {
						setSaveState('')
						void appAlert({title: '配额不足', message: quotaMessage('create_score', quota), confirmText: '知道了'})
						return
					}
					const newId = await cloud.saveNewScore(title, contentToSave)
					scoreIdRef.current = newId
					setCloudScoreId(newId)
					pendingSaveNotice = '已保存到你的编曲练习库 ' + new Date().toLocaleTimeString()
					history.replaceState(undefined, '', '#edit/' + newId)
					window.dispatchEvent(new HashChangeEvent('hashchange'))
				}
				// 曲名写回 `Dt:` 之后，编辑器里那份源码也得跟着换 —— 否则下一次保存
				// 又是从旧内容里取标题，用户刚起的名字在谱面上看不到（只在库里）。
				if (contentToSave !== content) {
					api.triggerReplaceContent(contentToSave)
				}
				api.triggerSaved(title)
				setCurrentXmlTitle(title)
				if (!silent) setSaveState('已保存 ' + new Date().toLocaleTimeString())
			} catch (err: any) {
				setSaveState('保存失败：' + (err?.message ?? err))
			}
		})
	}

	// 45 秒静默自动保存（仅云端曲谱）。别人的谱（另存副本场景）不自动跑 ——
	// 副本必须用户亲手点，静默每 45 秒刷一份是惊吓。
	useEffect(() => {
		const timer = setInterval(() => {
			const session = sessionRef.current
			const meta = openedMetaRef.current
			if (loggedIn && scoreIdRef.current && session) {
				// 只对**自己的**谱自动保存。别人的谱（另存副本场景）不自动跑 ——
				// 自动往别人曲谱上写是越权，后端也会拒绝，不如这里就别发。
				if (!meta.isMine) return
				saveToCloud({silent: true})
			}
		}, 45000)
		return () => clearInterval(timer)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedIn])

	// Ctrl+S 保存
	useEffect(() => {
		const onKeyDown = (evt: KeyboardEvent) => {
			if ((evt.ctrlKey || evt.metaKey) && evt.key.toLowerCase() == 's') {
				evt.preventDefault()
				saveToCloud()
			}
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// ---------- 历史版本 ----------
	async function openHistory() {
		if (!scoreIdRef.current) return
		setHistoryOpen(true)
		setHistoryList(null)
		// 每次打开都从"没在编辑"开始：上次编辑到一半关掉窗口，草稿不该留着
		setNoteEditingId(null)
		setNoteDraft('')
		try {
			setHistoryList(await cloud.listVersions(scoreIdRef.current))
		} catch (err) {
			// 取不到就显示空列表，别让面板卡在"加载中"
			console.warn('历史版本读取失败：', err)
			setHistoryList([])
		}
	}

	/** 点备注按钮 → 就地展开输入框，带上原有备注当草稿 */
	function startEditNote(version: {id: string, note: string}) {
		setNoteEditingId(version.id)
		setNoteDraft(version.note ?? '')
	}

	function cancelEditNote() {
		setNoteEditingId(null)
		setNoteDraft('')
	}

	/**
	 * 保存备注。
	 *
	 * 成功后**就地改这一条**，不重拉列表：重拉会让正在看的位置被冲掉，
	 * 也可能与"刚打开面板时的快照"不一致（这中间别人删了旧版本的话，
	 * 整行会突然消失，用户会以为自己的备注把版本搞没了）。
	 */
	async function saveNote(versionId: string) {
		if (noteSaving) return
		const note = noteDraft.trim()
		setNoteSaving(true)
		try {
			await cloud.setVersionNote(versionId, note)
			setHistoryList(list => list
				? list.map(v => (v.id === versionId ? {...v, note} : v))
				: list)
			setNoteEditingId(null)
			setNoteDraft('')
		} catch (err: any) {
			// 失败时保留草稿与输入框，别把用户刚打的字清掉
			await appAlert({
				title: '备注没保存上',
				message: err?.message ?? '请稍后重试。',
				confirmText: '知道了',
			})
		} finally {
			setNoteSaving(false)
		}
	}

	async function rollbackToVersion(version: {id: string, created_at: string | null, note: string}) {
		const sid = scoreIdRef.current
		if (!sid) return
		// 确认框里带上时间与备注：历史版本列表常常有好几条时间连着，
		// 只说"回滚到此版本？"等于让用户凭记忆核对点的是哪一条。
		const when = version.created_at ? new Date(version.created_at).toLocaleString() : '更早的时间'
		const detail = version.note ? `「${version.note}」` : ''
		// 站内确认弹窗（宣纸/暗墨，与保存分流那几处同一套）—— 别用原生 confirm。
		const ok = await appConfirm({
			title: '回滚到此版本？',
			message: `${when} 的版本${detail}。\n\n当前未保存的修改将丢失。回滚只是把这份历史内容载入编辑器，要点「保存」才真正生效。`,
			confirmText: '回滚',
			cancelText: '取消',
			danger: true,
		})
		if (!ok) return
		let data: {title: string, content: string}
		try {
			data = await cloud.loadVersion(version.id)
		} catch (err: any) {
			await appAlert({
				title: '版本读取失败',
				message: err?.message ?? '该版本不存在',
				confirmText: '知道了',
			})
			return
		}
		callRef(editorRef, api => {
			api.triggerOpen({path: data.title, content: data.content})
		})
		// 记到库里，而不是只放前端 state：换设备、隔天再打开，也要知道
		// "我当时停在哪一版"。写失败时下回打开会显示最新那条为「当前」，
		// 但回滚本身已经生效了 —— 为这个弹窗报错反而更让人困惑，记日志即可。
		try {
			await cloud.setActiveVersion(sid, version.id)
		} catch (err) {
			console.warn('「当前版本」没写进库：', err)
		}
		setHistoryOpen(false)
		setSaveState('已回滚到历史版本，请点击保存生效')
	}

	// ---------- MusicXML ----------
	// 检查器里那个「MusicXML」按钮走的是 `editorPrefs.onMusicXmlExport`，本站故意不挂：
	// 一份谱只需要一个导出出口，面板里再放一个只会让人犹豫点哪个。
	// （导出实现见下面的 handleMusicXmlDownload —— 顶栏入口目前按产品要求隐藏，
	// 开关是模块顶部的 `SHOW_RAW_EXPORTS`；组件库那个口子一直留着，别的宿主自便。）

	// 从编辑器当前内容直接导出 .musicxml 文件
	function handleMusicXmlDownload() {
		callRef(editorRef, api => {
			api.triggerBeforeSave()
			const content = api.getValue()
			const path = api.getFilename()
			const title = (typeof path === 'string' && path) || extractTitle(content) || ''
			try {
				const xml = buildStaffXml(content, title)
				downloadBlob(new Blob([xml], {type: 'application/vnd.recordare.musicxml+xml'}), (title || 'score') + '.musicxml')
			} catch (err: any) {
				void appAlert({title: '导出 MusicXML 失败', message: String(err?.message ?? err), confirmText: '知道了'})
			}
		})
	}

	// ---------- 五线谱预览（NMN → MusicXML → OSMD 渲染） ----------
	function buildStaffXml(content: string, title: string): string {
		const parsed = SparksNMN.parse(content)
		// 构造默认混音设置（与 PlayPanel.recreateControlData 等价的最小版）
		const controlData: any = {}
		for(const article of parsed.sequenced.score.articles) {
			if(article.type != 'music') continue
			for(const part of (article as any).parts) {
				controlData[part.signature.hash] = {
					control: {...controlDataPartDefault},
					signature: part.signature,
				}
			}
		}
		return MusicXmlExporter.exportMusicXml(parsed, controlData, title || UNTITLED_SCORE_TITLE, {source: watermark})
	}

	// 五线谱预览弹窗内直接下载 PDF：截 OsmdViewer 渲染容器 → jsPDF A4 分页
	async function exportStaffPdf() {
		const el = staffContainerRef.current
		if(!el) {
			await appAlert({title: '五线谱预览', message: '五线谱尚未渲染完成，请稍候再试。', confirmText: '知道了'})
			return
		}
		try {
			const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
				import('html2canvas'),
				import('jspdf'),
			])
			const canvas = await html2canvas(el, {scale: 2, backgroundColor: '#ffffff'})
			// 水印走 Canvas 2D 直接绘制：html2canvas 对 CSS 背景里的 SVG data URI
			// 支持不稳（水印层会拍成一张空白），所以不能像编辑页那样靠 DOM 覆盖层。
			drawWatermarkOnCanvas(canvas, watermark, false)
			const pdf = new jsPDF({unit: 'mm', format: 'a4'})
			const imgW = 210
			const imgH = canvas.height * imgW / canvas.width
			const pageCanvasH = Math.floor(canvas.width * 297 / imgW)
			const pages = Math.max(1, Math.ceil(canvas.height / pageCanvasH))
			for(let i = 0; i < pages; i++) {
				if(i > 0) pdf.addPage()
				const srcY = i * pageCanvasH
				const srcH = Math.min(pageCanvasH + 1, canvas.height - srcY)
				if(srcH <= 0) break
				const pc = document.createElement('canvas')
				pc.width = canvas.width
				pc.height = srcH
				pc.getContext('2d')!.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH)
				pdf.addImage(pc.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, imgW, imgH * srcH / canvas.height)
			}
			pdf.save((currentXmlTitle || 'score') + '.pdf')
		} catch (err: any) {
			void appAlert({title: 'PDF 导出失败', message: String(err?.message ?? err), confirmText: '知道了'})
		}
	}

	function openStaffPreview() {
		callRef(editorRef, api => {
			api.triggerBeforeSave()
			const content = api.getValue()
			const path = api.getFilename()
			const title = (typeof path === 'string' && path) || extractTitle(content) || ''
			try {
				setStaffXml(buildStaffXml(content, title))
				setStaffQpm(extractQpm(content))
				setStaffOpen(true)
			} catch (err: any) {
				void appAlert({title: '生成五线谱失败', message: String(err?.message ?? err), confirmText: '知道了'})
			}
		})
	}

	async function exportHtml() {
		try {
			const template = await getExportTemplate()
			callRef(editorRef, api => {
				const html = api.exportHtml(template, './core-resources/font', {watermark: watermark})
				downloadBlob(new Blob([html], {type: 'text/html'}), (currentXmlTitle || 'score') + '.html')
			})
		} catch (err: any) {
			void appAlert({title: 'HTML 导出失败', message: String(err?.message ?? err), confirmText: '知道了'})
		}
	}

	// ---------- 导出 PDF（隐藏 iframe 渲染导出 HTML → 整幅截图 → jsPDF 分页） ----------
	async function exportPdf() {
		setSaveState('正在生成 PDF…')
		try {
			const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
				import('html2canvas'),
				import('jspdf'),
			])
			const template = await getExportTemplate()
			const html = await new Promise<string>(resolve => {
				callRef(editorRef, api => resolve(api.exportHtml(template, './core-resources/font')))
			})

			// 隐藏 iframe：字体与脚本同源加载，避开主文档样式干扰
			const iframe = document.createElement('iframe')
			iframe.style.cssText = 'position:fixed;right:10000px;bottom:10000px;width:794px;height:1123px;border:0;visibility:hidden;'
			document.body.appendChild(iframe)
			iframe.srcdoc = html
			await new Promise<void>(resolve => {
				iframe.onload = () => resolve()
				// srcdoc 加载失败兜底
				window.setTimeout(resolve, 8000)
			})
			// 等待 iframe 内曲谱渲染与字体就绪
			await new Promise(r => window.setTimeout(r, 600))
			try {
				await (iframe.contentDocument as any)?.fonts?.ready
			} catch {}
			const doc = iframe.contentDocument
			if (!doc || !doc.body) {
				throw new Error('曲谱渲染失败')
			}

			// A4 比例画布（794px 宽），按内容总高整幅截图，避免逐页截图拼接错位
			const canvas = await html2canvas(doc.body, {
				width: 794,
				windowWidth: 794,
				scale: 2,
				backgroundColor: '#ffffff',
			})
			document.body.removeChild(iframe)
			// 导出 PDF 时不往 iframe 里注水印层（同 html2canvas 拍不出 SVG 背景的原因），
			// 改在整幅截图上直接绘制，之后再分页 —— 这样每一页都带水印，不会只在首页有。
			drawWatermarkOnCanvas(canvas, watermark, false)

			const pageW = 210
			const pageH = 297
			const pdf = new jsPDF({ unit: 'mm', format: 'a4' })
			const imgW = pageW
			const imgH = canvas.height * pageW / canvas.width
			const pageCanvasH = Math.floor(canvas.width * pageH / pageW)
			const pages = Math.max(1, Math.ceil(canvas.height / pageCanvasH))
			for (let i = 0; i < pages; i++) {
				if (i > 0) pdf.addPage()
				// 该页对应的源图区域（页高不是整数像素时向下多取 1px，防止页缝白线）
				const srcY = i * pageCanvasH
				const srcH = Math.min(pageCanvasH + 1, canvas.height - srcY)
				if (srcH <= 0) break
				const pageCanvas = document.createElement('canvas')
				pageCanvas.width = canvas.width
				pageCanvas.height = srcH
				const ctx = pageCanvas.getContext('2d')!
				ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH)
				pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, imgW, imgH * srcH / canvas.height)
			}
			pdf.save((currentXmlTitle || 'score') + '.pdf')
			setSaveState('PDF 已导出')
		} catch (err: any) {
			console.error(err)
			void appAlert({title: 'PDF 导出失败', message: String(err?.message ?? err), confirmText: '知道了'})
			setSaveState('')
		}
	}

	// ---------- 分享链接（只读） ----------
	/**
	 * 建一条分享链接并塞进列表，返回那一行；失败返回 null。
	 *
	 * 从 `generateShareLink` 里抽出来，是因为「分享到微信」也要用：卡片必须
	 * 指向一个**能打开**的地址，编辑页地址（`#edit/<id>`）要登录才看得到，
	 * 不能当分享目标 —— 所以没有链接时得先建一条。
	 */
	async function createShareLink(): Promise<{id: string, url: string, created_at: string} | null> {
		if (!cloudScoreId || !loggedIn) return null
		// 配额：先查后做，做成才记。后端 share/create 也会再查一次并拒绝超额 ——
		// 这层只是提前给友好提示，所以少了 recordUsage 也不会漏记账。
		const quota = await checkQuota('create_share')
		if (!quota.allowed) {
			void appAlert({title: '配额不足', message: quotaMessage('create_share', quota), confirmText: '知道了'})
			return null
		}
		setShareGenerating(true)
		try {
			// expires_at 由后端按 7 天算（core/views_scores.py 的 SHARE_TTL_DAYS），
			// 前端不再自己拼时间戳 —— 两处各写一份必然会漂。
			const link = await cloud.createShareLink(cloudScoreId)
			const row = {
				id: link.id,
				url: location.origin + location.pathname + '#share/' + link.id,
				created_at: new Date().toISOString(),
			}
			setShareLinks(prev => [row, ...prev])
			return row
		} catch (err: any) {
			void appAlert({title: '生成分享链接失败', message: err.message, confirmText: '知道了'})
			return null
		} finally {
			setShareGenerating(false)
		}
	}

	async function generateShareLink() {
		const row = await createShareLink()
		if (row) setShareOpen(true)
	}

	/**
	 * 分享卡片的标题：与保存流程**同一判据**（谱面 `Dt:` → 库名/文件名 → 占位名）。
	 *
	 * 不能直接读 `currentXmlTitle`：那是"打开这份谱时库里叫什么"，用户在
	 * 谱面里把 `Dt:` 改了之后它还是旧的，分享出去就是另一个名字。
	 */
	function editorDocTitle(): string {
		let content = ''
		callRef(editorRef, api => { content = api.getValue() })
		return resolveScoreTitle({content, path: currentXmlTitle ?? ''}).title
	}

	/**
	 * 分享到 PC 微信（浏览器直接唤起本机微信的会话/朋友圈）。
	 *
	 * 只在电脑上有效：微信侧要连本机客户端，手机上必然失败，所以那边直接引导
	 * 「复制链接」。失败一律弹框说清原因 —— 最常见的是"微信没开"，沉默失败
	 * 会让人以为点了没反应。
	 */
	async function shareToWechat(link?: {url: string}) {
		// 微信侧按用户限流 1 次/秒、5 次/分，连点只会吃 errcode 8
		if (shareWxBusy) return
		setShareWxBusy(true)
		setShareWxNotice('')
		try {
			// 没有可用链接就先建一条。**过期的不算可用** —— 分享出去的是别人点开
			// 就 404 的地址，比"先停下来建一条"糟得多。
			const now = Date.now()
			const usable = shareLinks.find(l => !l.expires_at || new Date(l.expires_at).getTime() > now)
			const target = link ?? usable ?? await createShareLink()
			if (!target) return
			await shareToWechatPc({
				url: target.url,
				title: editorDocTitle(),
				desc: '漫漫弹 · 打开就能看简谱、跟着弹',
			})
			setShareWxNotice('已唤起微信，请在微信弹出的窗口里选择好友')
		} catch (err: any) {
			await appAlert({
				title: '没有分享到微信',
				message: `${err?.message ?? String(err)}\n\n可以先用「复制」把链接发到微信。`,
				confirmText: '知道了',
			})
		} finally {
			setShareWxBusy(false)
		}
	}

	async function deleteShareLink(id: string) {
		if (!await appConfirm({title: '删除分享链接', message: '确定要删除此分享链接吗？删除后原链接立即失效。', confirmText: '删除', cancelText: '取消', danger: true})) return
		try {
			await cloud.deleteShareLink(id)
			setShareLinks(prev => prev.filter(link => link.id !== id))
		} catch (err: any) {
			void appAlert({title: '删除分享链接失败', message: err.message, confirmText: '知道了'})
		}
	}

	async function loadShareLinks() {
		if (!cloudScoreId) return
		try {
			const rows = await cloud.listShareLinks(cloudScoreId)
			setShareLinks(rows.map(row => ({
				...row,
				url: location.origin + location.pathname + '#share/' + row.id,
			})))
		} catch (err) {
			console.error('加载分享链接失败:', err)
		}
	}

	// 「这是不是别人的谱」—— 保存按钮分流与跟弹限制共用同一个判定。
	//
	// ⚠️ 两类要分开判，合并成一个布尔会漏掉首页样张：
	//   · 有云端行 → 用后端返回的 isMine（另存的副本 creator 是原作者 manmantan，
	//     若改按 creator 判，副本永远显示"保存到编曲练习库"、每次保存都再多一份）；
	//   · 无云端行（首页样张 / 本地草稿）→ 还没有 isMine 可言，
	//     只能看 creator 署名：非空且不等于自己，就说明是别人写的谱。
	//
	// ⚠️ 必须声明在 editorPrefs **之前** —— 它是普通 const 不是 hook，
	//   在后面声明会撞上 TDZ（编辑器的跟弹按钮直接报
	//   "Cannot access 'isOthersScore' before initialization"）。
	const isOthersScore = props.session != null && (
		cloudScoreId
			? !openedMeta.isMine
			: !!openedMeta.creator && openedMeta.creator !== displayName(props.session)
	)

	// 浮窗菜单里的「查看五线谱」「导出 PDF」：入口在组件库那边，动作仍在本页
	//（与顶栏「五线谱预览」「导出 PDF」是同一套函数）。editorPrefs 是 memo 出来的，
	// 直接把函数塞进去会把它永久锁在首次渲染那份闭包上（exportPdf 里要用
	// currentXmlTitle / watermark），所以转一手 ref，点击时取当次渲染的最新版本。
	const staffPreviewRef = useRef(openStaffPreview)
	const pdfExportRef = useRef(exportPdf)
	staffPreviewRef.current = openStaffPreview
	pdfExportRef.current = exportPdf

	// ⚠️ 必须 memo。IntegratedEditor 拿 editorPrefs / colorScheme 当 contextValue 的依赖，
	//    每次渲染给新对象 = 每帧重建一张 JSS 样式表（useRecreatedStyles 里那个 useMemo）。
	const editorPrefs = useMemo(() => ({
		modifyTitle: {
			default: LNG('title.default'),
			new: LNG('title.new'),
			newDirty: LNG('title.newDirty'),
			clean: LNG('title.new'),
			dirty: LNG('title.newDirty'),
		},
		// 这里原本挂 importantWarning —— 上游开源版那段「试用模式 / 不支持文件保存 / 建议下载
		// 桌面版 / Ctrl+S 快捷键」的欢迎页横幅。本站是正式 SaaS，那些提示既过时又误导，已整段
		// 删除（该字段可选，PreviewView 只在有值时才渲染，删掉不残留空容器）。
		temporarySave: true,
		isMobile: isMobile,
		inspectorOpen: false,
		instrumentSourceUrl: './core-resources/audio',
		// 源码面板那行标题：不指望用户自己看出下半屏是"可编辑的文本"
		sourcePaneHeader: {title: '简谱源码', note: '纯文本 · 可整段复制'},
		// 谱面列居中（设计稿 `.preview` 的 `justify-content:center`）。
		// 编辑/曲谱区域改回上下分屏（用户要求），不再传 sourcePaneSide: 'left'。
		//
		// ⚠️ **这个数不是"容器宽度"，它就是谱面字号**。谱面是按列宽等比缩放的
		//    （`core/equifield/equifield.ts:33`：`font-size = 容器宽 / 64` —— 谱面、分页、
		//    导出全都建立在这个 relation 上），所以列宽砍一半，音符就砍一半。
		//    设计稿 `.score{max-width:520px}` 是**样张 HTML 的字面宽度**，那张样张的文字是 px 写死的、
		//    不随列宽缩放，照搬过来会把真谱子缩到 7.7px（实测，1440 视口下）—— 别照搬。
		//    1000 是组件默认值，实测在 1440 视口下谱面 11.7px（拆分）/ 14.8px（预览模式），
		//    与代码区 14.5px 基本齐平。
		previewMaxWidth: 1000,
		previewAlign: 'center' as const,
		// 状态栏那三个药丸原来只是三撮灰字，选中态还与底色同色 —— 看不出是控件。
		// 见 `IntegratedEditorPrefs.displayModeChips`：换成描边实心块，桌面版不受影响。
		displayModeChips: true,
		// 谱面水印文案（后台可改）。初值是组件默认常量，挂载后 fetchWatermark 覆盖，
		// 所以这里要把 watermark 列进依赖，读到新值时 editorPrefs 重建、预览跟着换。
		watermark: watermark,
		// 非本人曲谱：跟弹 / 视弹都受限 —— 只能用自己编曲练习库内的曲谱，可先另存到自己库。
		// 判定与下方 isOthersScore 同一套：打开别人的云端谱、首页样张、广场谱都算非本人。
		// 受限时悬浮菜单的「跟弹」「视弹」都改成弹站内提示（走 appAlert 单键），
		// 并从「更多」面板移除这两个页；试听不受影响。
		onPracticeRestricted: isOthersScore ? () => {
			void appAlert({
				title: '只能跟弹/视弹自己的曲谱',
				message: '这份曲谱不是你编曲练习库里的曲子，暂时不能跟弹或视弹。\n可以先点「保存到编曲练习库」把它另存到自己的库，之后就能用了。',
				confirmText: '知道了',
			})
		} : undefined,
		// 浮窗菜单里的另外两个宿主动作（顶栏「五线谱预览」「导出 PDF」的同一个函数，
		// 走 ref 取最新闭包 —— 见上方 staffPreviewRef 的说明）。
		onStaffPreview: () => staffPreviewRef.current(),
		onPdfExport: () => void pdfExportRef.current(),
		// 会员门禁：菜单里「跟弹 / 自动左手伴奏 / 更多」带 PLUS 徽章，
		// 非 Plus 点击 → 弹升级框（判定与文案都在 saas/plan.ts，别在这里另写一份）。
		isPlus: isPlus,
		onPlusRequired: openUpgrade,
		// 音色校准：只读用户档案里的识别阈值（随账号走）。校准入口在「编曲练习库」页的
		// 「设置」菜单里，这里只负责让视弹面板挂载时读回阈值。没登录时后端返回空/401，
		// 由 profile.ts 的 try/catch 兜底，不影响其它功能。
		loadCalibration: () => fetchPitchCalibration(),
	// 表里只剩常量与 `isMobile`。原先收着一份 MusicXML 回调，它闭包捕获 `currentXmlTitle`
	// （导出文件名用它），所以才盯着这个依赖；不挂它之后也就跟着撤了 ——
	// 其余引到 currentXmlTitle 的（导出 HTML/PDF）都在 headerActions 里，不走这张表。
	// （导出 HTML 现在是条件项，见 `SHOW_RAW_EXPORTS`。）
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}), [isMobile, LNG, watermark, openedMeta, props.session, isPlus, openUpgrade])

	// 顶栏动作表：桌面按顺序铺开，移动端只留「保存 / ⋯」（`文件▾` 已无内容，
	// PageHeader 见 `fileActions.length > 0` 才渲染，所以它跟着一起消失了）
	// 保存按钮按归属分流（判定见上方 isOthersScore）：别人的谱 → 另存副本；
	// 自己的谱 → 自动同步云端。未登录一律「保存」（点击时引导登录）。
	const saveLabel = isOthersScore ? '保存到编曲练习库' : '保存'
	const headerActions: HeaderAction[] = [
		// 返回：有来源历史则回上一页，否则回首页（直开编辑器链接时 history 为空）。
		// mobileRow：移动端顶栏只有 3 个位置，返回必须直显（用户实测收进 ⋯ 不方便）
		{key: 'back', label: '返回', mobileRow: true, onClick: () => {
			if(window.history.length > 1) {
				window.history.back()
			} else {
				navigateHash('#')
			}
		}},
		{key: 'save', label: saveLabel, variant: 'primary', mobileRow: true, onClick: () => saveToCloud()},
		...(cloudScoreId ? [{key: 'history', label: '历史版本', onClick: openHistory}] : []),
		// 「五线谱预览」「导出 PDF」「导入 MusicXML」三个入口已按产品要求从顶栏撤掉
		// （2026-10-29）：
		//   · 五线谱预览 / 导出 PDF  → 右下角浮窗菜单（见 editorPrefs.onStaffPreview /
		//     onPdfExport，函数仍是下面这两个 openStaffPreview / exportPdf）
		//   · 导入 MusicXML          → 编曲练习库页面，且只对管理员显示
		// 于是 `file: true` 的项一个都不剩，「文件▾」按钮自动消失（不必再加开关）。
		// 「导出 MusicXML」「导出 HTML」：入口按产品要求隐藏，实现保留（开关见模块顶部
		// `SHOW_RAW_EXPORTS`）。写成条件展开而不是注释掉两行 —— 这样两个 handler 仍被
		// 引用，`npm run lint` 不会报"声明了没用到"，恢复时也只改一个常量。
		...(SHOW_RAW_EXPORTS ? [
			{key: 'xml', label: '导出 MusicXML', file: true, onClick: handleMusicXmlDownload},
			{key: 'html', label: '导出 HTML', file: true, onClick: exportHtml},
		] as HeaderAction[] : []),
		...(cloudScoreId && loggedIn ? [{key: 'share', label: '分享', onClick: () => {loadShareLinks(); setShareOpen(true)}}] : []),
		// 写谱写到一半忘了记号怎么打，出口在这里。桌面铺在登录/编曲练习库之前（写谱页比编曲练习库
		// 更常去翻教程）；移动端不占那三个位置，落进 ⋯ 抽屉。
		// 这里是全站唯一一个真链接动作：新开标签页读教程，**正在编辑的谱子原样留在本页**，
		// 不会被导航走。href 只写裸 hash —— 相对 URL 按当前文档解析，路径与 query 都保留，
		// 天然跟着 --base=./ 的子路径部署走（手拼 location.origin + pathname 反而会丢 query）。
		{key: 'tutorial', label: '教程', href: '#tutorial', newTab: true},
		loggedIn
			? {key: 'library', label: '编曲练习库', variant: 'outline' as const, onClick: () => navigateHash('#library')}
			: {key: 'login', label: '登录', variant: 'outline' as const, onClick: () => navigateHash('#login')},
	]

	return <PageHeader
		// 曲名：云端曲谱用真标题，本地暂存用与保存逻辑一致的兜底名
		//（不要用 LNG('title.default')，那是旧品牌名「Sparks NMN 试用模式」）
		doc={currentXmlTitle || UNTITLED_SCORE_TITLE}
		saveState={saveState}
		actions={headerActions}
		onHome={() => navigateHash('')}
		// 邮箱只在登录后传：没登录时传一个空 `<span>` 也是"传了"，
		// PageHeader 会当它存在而把设置▾ 铺开 —— 菜单里就只剩主题加一个空信息行。
		trailing={loggedIn ? <span className={classes.userEmail}>{userEmail}</span> : undefined}
		// 退出交给顶栏自己摆：桌面在设置▾ 里（与邮箱同处），窄屏没有它的位置、落进 ⋯ 抽屉。
		// 原来它是 actions 里的一项，会被排到邮箱左边去。没登录就不传 —— 不传则整块不渲染。
		onLogout={loggedIn ? props.onLogout : undefined}
		// 这一页工具条动作最多（保存 / 历史版本 / 五线谱预览 / 分享 / 教程 / 编曲练习库 + 文件▾），
		// 主题开关再占三个按钮位就挤了 —— 把 主题 / 邮箱 / 退出 收进「设置▾」。
		// 未登录时邮箱与退出都不存在，收起来只剩主题，那时退回平铺（见 PageHeader.collapseSettings）。
		settingsMenu>

		{/* 站内确认对话框的宿主：保存分流的三处弹窗（登录引导 / 同名覆盖 / 打开已有）
		    都从这里出，跟宣纸/暗墨主题与站点按钮语言走 —— 别再回退到原生 confirm */}
		<AppDialogHost />

		{/* 升级 Plus 会员：浮窗菜单里被锁的三个入口（跟弹 / 自动左手伴奏 / 更多）
		    点了就开它。放在编辑器外面 —— 它是站点级弹窗，不该被编辑器布局约束 */}
		<UpgradeDialog
			open={upgrade.open}
			feature={upgrade.feature}
			user={props.session}
			onClose={() => setUpgrade({open: false})}
		/>

		<div className={classes.editorWrap}>
			<React.Suspense fallback={<div className={classes.editorLoading}>编辑器加载中…</div>}>
				<IntegratedEditor
					ref={attachEditor}
					language={NMNI18n.languages.zh_cn}
					editorPrefs={editorPrefs}
					colorScheme={editorColors}
					onRequestSave={() => saveToCloud()}
				/>
			</React.Suspense>
		</div>

		{/* 历史版本 / 分享链接 模态框 */}
		{(historyOpen || shareOpen) && <div className={classes.modalBackdrop} onClick={() => {setHistoryOpen(false); setShareOpen(false)}}>
			<div className={classes.modal} onClick={evt => evt.stopPropagation()}>
				<button className={classes.modalClose} onClick={() => {setHistoryOpen(false); setShareOpen(false)}}>关闭</button>
				{historyOpen && <>
					<h3 className={classes.modalTitle}>历史版本</h3>
					{historyList == null && <div className={classes.empty}>加载中…</div>}
					{historyList != null && historyList.length == 0 && <div className={classes.empty}>暂无历史版本（保存后会自动留档）</div>}
					{historyList != null && historyList.map(v => <div key={v.id} className={classes.versionBlock}>
						<div className={classes.versionItem}>
							<span className={classes.versionTitle}>{v.title || '未命名'}</span>
							{/* 备注按钮摆在时间戳**前面**：先看"这版改了什么"，再看"什么时候"。
							    编辑时隐藏 —— 编辑区在下面整行铺开，同时留着按钮会多一个可点的干扰项。 */}
							{noteEditingId != v.id && <button
								type="button"
								className={v.note ? classes.versionNote : classes.versionNoteEmpty}
								title={v.note ? `${v.note}（点击修改）` : '给这个版本写一句备注'}
								onClick={() => startEditNote(v)}>
								{v.note || '＋备注'}
							</button>}
							<span className={classes.versionTime}>{fmtVersionTime(v.created_at)}</span>
							{/* 「当前」与「回滚」占同一个位置、同一套盒子：这一版已经载入编辑器了，
							    点"回滚到自己"没有意义（还会把未保存的改动冲掉），所以那个动作就不该在。
							    用文字而不是禁用按钮 —— 禁用态半透明，看起来像"暂时点不了"，
							    而这里要表达的是"你已经在这一版上"。
							    is_active 由后端给：回滚过 → 那一版；没回滚过 → 最新的一条。 */}
							{v.is_active
								? <span className={classes.versionCurrent}>当前</span>
								: <button className={`${classes.tb} ${classes.tbOutline}`} onClick={() => rollbackToVersion(v)}>回滚</button>}
						</div>
						{noteEditingId == v.id && <div className={classes.noteEditor}>
							<input
								className={classes.noteInput}
								value={noteDraft}
								autoFocus
								placeholder="这版改了什么？例如：第三段和弦改成 C-Am-F-G"
								onChange={evt => {
									// 按码点截断，与后端 len() 的口径一致（length 会把 emoji 算 2 个）
									const chars = Array.from(evt.target.value)
									setNoteDraft(chars.length > NOTE_MAX_CHARS ? chars.slice(0, NOTE_MAX_CHARS).join('') : evt.target.value)
								}}
								onKeyDown={evt => {
									if (evt.key == 'Enter') {
										evt.preventDefault()
										void saveNote(v.id)
									} else if (evt.key == 'Escape') {
										evt.preventDefault()
										cancelEditNote()
									}
								}}
							/>
							<span className={classes.noteCount}>{Array.from(noteDraft).length}/{NOTE_MAX_CHARS}</span>
							<button className={`${classes.tb} ${classes.tbPrimary}`} onClick={() => void saveNote(v.id)} disabled={noteSaving}>
								{noteSaving ? '保存中…' : '保存'}
							</button>
							<button className={`${classes.tb} ${classes.tbOutline}`} onClick={cancelEditNote} disabled={noteSaving}>取消</button>
						</div>}
					</div>)}
					{historyList != null && historyList.length > 0 && <div className={classes.versionHint}>
						{isPlus
							? `Plus 会员保留最近 ${VERSION_VISIBLE_PLUS} 个历史版本，可直接回滚。`
							: `免费会员显示最近 ${VERSION_VISIBLE_FREE} 个历史版本；更早的版本没有被删除，升级后能看到最近 ${VERSION_VISIBLE_PLUS} 个历史版本。`}
						{!isPlus && <button type="button" className={classes.versionHintUp} onClick={() => openUpgrade('history')}>升级 Plus</button>}
					</div>}
				</>}
				{shareOpen && <>
					<h3 className={classes.modalTitle}>分享链接（7 天有效）</h3>
					<div className={classes.shareActions}>
						<button className={`${classes.tb} ${classes.tbPrimary}`} onClick={generateShareLink} disabled={shareGenerating}>
							{shareGenerating ? '生成中…' : '生成新链接'}
						</button>
						{/* 微信分享：直接唤起电脑上登录着的微信，不经过剪贴板。
						    thumburl 是微信侧的必填项，由后端连同 ticket 一起下发。 */}
						<button className={`${classes.tb} ${classes.tbOutline}`}
							onClick={() => void shareToWechat()}
							disabled={shareWxBusy || shareGenerating}>
							{shareWxBusy ? '正在唤起微信…' : '分享到微信'}
						</button>
					</div>
					<p className={classes.shareHint}>
						直接分享需要电脑上登录着微信 4.1.0 及以上版本；手机上请用「复制」把链接发到微信。
					</p>
					{shareWxNotice && <div className={classes.shareNotice}>{shareWxNotice}</div>}
					{shareLinks.length == 0 && <div className={classes.empty}>还没有分享链接</div>}
					{shareLinks.map(link => <div key={link.id} className={classes.shareLinkRow}>
						<span className={classes.shareUrl}>{link.url}</span>
						<button className={`${classes.tb} ${classes.tbOutline}`} disabled={shareWxBusy}
							onClick={() => void shareToWechat(link)}>微信</button>
						<button className={`${classes.tb} ${classes.tbOutline}`} onClick={() => {navigator.clipboard.writeText(link.url); void appAlert({title: '已复制', message: '分享链接已复制到剪贴板。', confirmText: '知道了'})}}>复制</button>
						<button className={`${classes.tb} ${classes.tbOutline}`} onClick={() => deleteShareLink(link.id)}>删除</button>
					</div>)}
				</>}
			</div>
		</div>}

			{/* 五线谱预览模态框（OSMD 渲染，歌词已随 MusicXML 带出） */}
			{staffOpen && <div className={classes.modalBackdrop} onClick={() => setStaffOpen(false)}>
				<div className={classes.modal} style={{width: 900}} onClick={evt => evt.stopPropagation()}>
					<button className={classes.modalClose} onClick={() => setStaffOpen(false)}>关闭</button>
					<h3 className={classes.modalTitle}>五线谱预览</h3>
					<OsmdViewer xmlText={staffXml} qpm={staffQpm} onContainerReady={el => { staffContainerRef.current = el }} />
					<div style={{marginTop: 12, display: 'flex', gap: 8}}>
						<button className={`${classes.tb} ${classes.tbPrimary}`} onClick={exportStaffPdf}>下载 PDF</button>
						<span style={{fontSize: 12, opacity: 0.6, alignSelf: 'center'}}>由曲谱实时生成，歌词与多段歌词已一并导出。</span>
					</div>
				</div>
			</div>}
	</PageHeader>
}

// 只读分享查看页：任何人持链接即可查看，无需登录
function ShareViewPage(props: {shareId: string}) {
	const classes = useStyles()
	const [editorRef, attachEditor, editorAttached] = useEditorRef()
	const isMobile = useIsMobile()
	const editorColors = useEditorColorScheme()
	// 谱面水印：初值组件默认常量，挂载后读后台覆盖（失败保持默认）。
	// 分享页是只读视图，同样要铺水印，且与后台设置一致。
	const [watermark, setWatermark] = useState(WATERMARK_TEXT)
	useEffect(() => {
		let cancelled = false
		fetchWatermark().then(text => {
			if(!cancelled) setWatermark(text)
		})
		return () => { cancelled = true }
	}, [])
	// 同样要 memo：IntegratedEditor 拿它当 contextValue 的依赖
	const sharePrefs = useMemo(() => ({
		displayMode: 'preview' as const,
		temporarySave: false,
		isMobile,
		inspectorOpen: false,
		instrumentSourceUrl: './core-resources/audio',
		watermark: watermark,
		// 分享页看的是别人的谱 —— 非本人，跟弹/视弹同样受限（只能用自己库内的曲谱），
		// 点这两项都弹站内提示，引导先另存到自己的库。
		onPracticeRestricted: () => {
			void appAlert({
				title: '只能跟弹/视弹自己的曲谱',
				message: '这份曲谱不是你编曲练习库里的曲子，暂时不能跟弹或视弹。\n可以先把它另存到自己的编曲练习库，之后就能用了。',
				confirmText: '知道了',
			})
		},
	}), [isMobile, watermark])
	const [state, setState] = useState<
		{status: 'loading'} | {status: 'error', message: string} | {status: 'ok', title: string, content: string}
	>({status: 'loading'})

	useEffect(() => {
		(async () => {
			// 访客可读 —— 分享链接的用途就是这个。取不到多为「不存在或已过期」。
			try {
				const row = await cloud.loadSharedScore(props.shareId)
				setState({status: 'ok', title: row.title ?? '分享曲谱', content: row.content ?? ''})
			} catch (err: any) {
				setState({
					status: 'error',
					message: err?.message ?? '分享链接不存在或已过期',
				})
			}
		})()
	}, [props.shareId])

	useEffect(() => {
		// 两个条件缺一不可：内容还没取回来，或者编辑器还没挂上，都不该灌。
		// 编辑器那个条件同样不能省 —— 这枪打在空处不会有第二次机会（`state` 不会再变），
		// 分享页会停在一个空编辑器上。
		if (editorAttached && state.status == 'ok') {
			callRef(editorRef, api => {
				api.triggerOpen({path: state.title, content: state.content})
			})
		}
	}, [state, editorAttached])

	if (state.status == 'loading') {
		return <div className={classes.loading}>加载中…</div>
	}
	if (state.status == 'error') {
		return <div className={classes.loading}>
			<div style={{textAlign: 'center'}}>
				<div style={{marginBottom: 16}}>{state.message}</div>
				<button className={`${classes.tb} ${classes.tbPrimary}`} onClick={() => navigateHash('#edit')}>打开编辑器</button>
			</div>
		</div>
	}

	return <PageHeader
		doc={(state.title || '分享曲谱') + ' · 只读分享'}
		onHome={() => navigateHash('')}
		actions={[
			{key: 'edit', label: '用编辑器打开', variant: 'outline', onClick: () => navigateHash('#edit')},
			{key: 'login', label: '登录', variant: 'primary', mobileRow: true, onClick: () => navigateHash('#login')},
		]}>
		<div className={classes.editorWrap}>
			<React.Suspense fallback={<div className={classes.editorLoading}>编辑器加载中…</div>}>
				<IntegratedEditor
					ref={attachEditor}
					language={NMNI18n.languages.zh_cn}
					editorPrefs={sharePrefs}
					colorScheme={editorColors}
				/>
			</React.Suspense>
		</div>
	</PageHeader>
}

// 重置密码：邮件链接形如 #/reset?uid=…&token=…，凭 token 设置新密码。
//
// ⚠️ 与原 Supabase 版的机制不同：那边靠邮件链接里带的**登录态**跳进来
// （supabase-js 把 access_token 塞进 URL 片段），所以页面只要判断"有没有
// session"。现在用显式的 uid + token —— token 一次性、可过期、改密码后
// 立即失效（后端 default_token_generator.token_changed 主动作废）。
function ResetPasswordPage() {
	const classes = useStyles()
	const [password, setPassword] = useState('')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState('')
	const [done, setDone] = useState(false)

	// 从 hash 的 query 里取 uid / token。
	// 解析时机要早：token 是一次性的，晚一刻都可能已被用掉；
	// 且首次渲染就要决定"该显示输入框还是错误页"，不能等异步。
	const {uid, token} = useMemo(() => {
		const raw = location.hash.replace(/^#\/reset\??/, '')
		return {
			uid: new URLSearchParams(raw).get('uid') ?? '',
			token: new URLSearchParams(raw).get('token') ?? '',
		}
	}, [])
	const valid = !!uid && !!token

	async function submit() {
		setError('')
		if (password.length < 6) {
			setError('密码至少需要 6 位')
			return
		}
		setBusy(true)
		try {
			await confirmPasswordReset(uid, token, password)
			// 重置后清掉 hash 里的 token：留在地址栏会被复制走、也会进浏览器历史
			history.replaceState(undefined, '', location.pathname + location.search + '#/login')
			setDone(true)
			setTimeout(() => navigateHash('#login'), 1500)
		} catch (err: any) {
			setError(translateAuthError(err.message))
		} finally {
			setBusy(false)
		}
	}

	return <div className={classes.loading}>
		<div className={classes.card}>
			{!valid
				? <>
					<div className={classes.cardTitle}>重置链接无效</div>
					<div className={classes.cardSub}>链接可能已过期或不完整，请回到登录页重新发送重置邮件。</div>
					<button className={`${classes.tb} ${classes.tbPrimary}`} onClick={() => navigateHash('#login')}>去登录</button>
				</>
					: done
						? <div style={{fontSize: 15, textAlign: 'center', padding: '16px 0'}}>密码已重置，即将前往登录页，请使用新密码登录…</div>
					: <>
						<div className={classes.cardTitle}>设置新密码</div>
						<div className={classes.cardSub}>请输入新的登录密码</div>
						{error && <div className={classes.formError}>{error}</div>}
						<div style={{marginBottom: 14}}>
							<input className={classes.input}
								type="password" value={password}
								onChange={e => setPassword(e.target.value)}
								onKeyDown={e => e.key === 'Enter' && submit()}
								placeholder="••••••" />
						</div>
						<button className={`${classes.tb} ${classes.tbPrimary}`} style={{width: '100%', padding: '11px 0'}}
							onClick={submit} disabled={busy}>
							{busy ? '请稍候…' : '确认重置'}
						</button>
					</>}
		</div>
	</div>
}

/**
 * 整页都不画谱、因此**不必等乐谱字体**的路由。
 *
 * 取**白名单**而不是黑名单：新加一条路由忘了登记，代价只是"多闪一个 splash"（慢），
 * 而不是"拿回退字形把谱画错"（错，而且会永久毒化 `DomPaint` 的 measure 缓存，
 * 见 `coreFonts.ts`）。默认等待，出错方向才是安全的。
 *
 * 这几页一个音符都不画：首页那张"谱面样张"是纯文本 + CSS 画的八度点，
 * 编曲练习库的图标是内联 svg，登录页与重置密码页更是全文字。
 * 指南（`tutorial`）正文里的实时乐谱**不由这里兜** —— 那是 `NmnLive` 的 `ScoreCanvas`
 * 自己按块等字体，整篇文字不该为某一处谱面拖着。
 *
 * `share` 不在此列：分享页整页就是一张谱。
 */
const FONT_FREE_PAGES: Route['page'][] = ['home', 'login', 'reset', 'library', 'tutorial', 'admin']

export function TestApp() {
	const [route, setRoute] = useState<Route>(() => parseHash())
	const [session, setSession] = useState<CurrentUser | null>(null)
	const [authReady, setAuthReady] = useState(false)
	const fontsReady = useCoreFontsReady()
	const classes = useStyles()

	/**
	 * 指南页整块懒加载。
	 *
	 * 12 篇 MDX + KaTeX + 那 165 处实时乐谱是本站**最大的一块**静态资源，
	 * 而首页与编辑器压根用不到它 —— 不切开的话，来写谱的人得先下载完整本教程。
	 * 只有真的进 `#tutorial` 才 import 那一块。
	 */
	const tutorialPage = () => (
		<React.Suspense fallback={<div className={classes.loading}>加载中…</div>}>
			<DocsPage
				slug={route.page === 'tutorial' ? route.slug : undefined}
				onNavigate={navigateHash}
			/>
		</React.Suspense>
	)

	/**
	 * 退出登录 —— 全站统一入口（编辑器 / 编曲练习库 / 设置页 / 后台共用）。
	 *
	 * **先导航、后清会话**，顺序不能反：
	 *   1. 编曲练习库有个「未登录 → 送登录页」的 effect（见下方）。会话一清掉
	 *      它就会把用户抢去 `#login`，然后我们才慢悠悠导航去首页 —— 用户会先
	 *      瞥见一眼登录页。先 `navigateHash('')` 让 route.page 离开 library，
	 *      那个 effect 的条件就不再成立。
	 *   2. 清会话是纯后台动作，不等它返回：hash 路由没有页面卸载，fetch 照常
	 *      发完。真失败了（断网 / 后端重启）人就留在首页，顶栏仍显示已登录，
	 *      再点一次退出即可 —— 不为这个弹窗打断导航。
	 *
	 * 原来四个页面各写 `() => logout()`：谁都不导航，退出后人还停在原页面
	 * （练习库靠 effect 兜到登录页，编辑器里直接以未登录态留着）。
	 */
	const handleLogout = () => {
		navigateHash('')
		void logout().catch(err => {
			console.warn('退出登录请求失败（会话可能仍在）：', err)
		})
	}

	useEffect(() => {
		const onHash = () => setRoute(parseHash())
		window.addEventListener('hashchange', onHash)
		return () => window.removeEventListener('hashchange', onHash)
	}, [])

	/**
	 * 官网「启动 / 试一试」链接带 `?load-example=<name>.spnmn`（只挂在首次进入的
	 * search 上，hash 路由不影响它）。把那份示例塞进 pendingDoc，再送到编辑器 ——
	 * 与首页推荐曲库的 openExample() 走完全相同的交接通路。
	 * 只在挂载时读一次：编辑器内再点官网链接的场景不存在，不值得监听。
	 */
	useEffect(() => {
		const exampleFile = getQueryVariable('load-example')
		if (!exampleFile) return
		// 用完即从地址栏抹掉，刷新/分享链接不会再重放一次载入
		history.replaceState(null, '', location.pathname + location.hash)
		findExampleByFile(exampleFile) && (async () => {
			try {
				const content = await fetchExampleScore(findExampleByFile(exampleFile)!)
				setPendingDoc({title: findExampleByFile(exampleFile)!.title, content})
				navigateHash('#edit')
			} catch (err: any) {
				console.warn('load-example 载入失败', err)
			}
		})()
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	/**
	 * 预取编辑器 chunk。`#edit` / `#share` 会先卡在下面那道字体闸门上（那批记谱字体
	 * 在慢网下要下几十秒），趁那段空档把编辑器也拉起来 —— 否则要等字体全下完才轮到它，
	 * 白白多等一个往返。`import()` 自己会去重，重复调不出第二份请求。
	 *
	 * 放在 effect 而不是渲染里：渲染期间发起请求是副作用，StrictMode 下每次渲染都跑。
	 */
	useEffect(() => {
		if (!FONT_FREE_PAGES.includes(route.page)) editorChunk()
	}, [route.page])

	useEffect(() => {
		// onSessionChange 会立即回报一次当前态，之后登录/登出时再推。
		// authReady 跟着第一次回调置位 —— 与原 supabase 版的就绪时机一致。
		const off = onSessionChange(u => {
			setSession(u)
			setAuthReady(true)
		})
		return off
	}, [])

	// 未登录闯进编曲练习库 → 送登录页。
	// 放 effect 而不是在渲染里直接 navigateHash：渲染期间改 location.hash 会当场触发
	// hashchange → setState，React 会报"渲染中更新状态"，认证态来回变时还可能打转。
	useEffect(() => {
		if (authReady && route.page === 'library' && !session) navigateHash('#login')
	}, [authReady, route, session])

	/**
	 * 当前登录者是不是管理员。
	 *
	 * 这道判断只决定「给不给后台入口 / 渲不渲染后台页」，**不是安全边界** ——
	 * 真正的拦截在 admin.sql 的 RLS 与 RPC 里，前端这道绕过去也只是看不到页面、
	 * 拿不到任何数据。两边都要有：只有后端的话正常用户根本不知道后台存在，
	 * 只有前端的话任何人都能改 localStorage 摸进来看结构。
	 */
	const [isAdmin, setIsAdmin] = useState(false)
	useEffect(() => {
		let cancelled = false
		fetchIsAdmin(session).then(v => {
			if(!cancelled) setIsAdmin(v)
		})
		return () => {
			cancelled = true
		}
	// 依赖登录用户的 id：换人登录才需要重算管理员标记，
	// 昵称变化不该触发（is_admin 不随昵称变）。
	}, [session?.id])

	/**
	 * 字体闸门。放在认证那一段**之前**：`#edit` / `#share` 本来就得等字体，
	 * 时序与改动前逐帧相同（静态首屏态 → 字体 splash → 认证「加载中…」→ 内容），
	 * 只是中间那屏由 React 出。
	 *
	 * `fontsReady` 是单向的（`fontPhase` 只前进），所以这道门只会开一次，
	 * 不会把已经挂着的 `EditorPage` 卸下来 —— 那会连它没保存的草稿一起丢掉。
	 */
	if (!FONT_FREE_PAGES.includes(route.page) && !fontsReady) return <CoreFontSplash />

	if (!authReady) {
		// 分享页、重置密码页与指南不依赖登录态，提前渲染 ——
		// 指南尤其不能等：它是最可能有陌生访客直接打开的一页，
		// 让对方先盯着一个「加载中…」看 Supabase 往返没有必要。
		if (route.page == 'share') return <ShareViewPage shareId={route.shareId} />
		if (route.page == 'tutorial') return tutorialPage()
		return <div className={classes.loading}>加载中…</div>
	}

	if (route.page == 'share') return <ShareViewPage shareId={route.shareId} />
	if (route.page == 'reset') return <ResetPasswordPage />
	if (route.page == 'tutorial') return tutorialPage()

	if (route.page == 'home') return <>
		{/* 首页右上角（主题开关右侧）的昵称与退出：会话与退出处理都在本组件，
		    这里只注入 —— HomePage 自己不碰会话状态。 */}
		<HomePage loggedIn={!!session} user={session} onLogout={handleLogout}
			onNavigate={navigateHash} />
		{/* 首页通知弹窗。放在 HomePage **之后**：通知是这一页的附加物，
		    不该挡在曲谱样张前面 —— 首屏先给内容，弹窗再叠上去。 */}
		<NoticeModal />
	</>

	if (route.page == 'admin') {
		// 未登录 / 非管理员一律送回编曲练习库，与编曲练习库未登录时的处理一致。
		// 这里**不**显示"你不是管理员"之类的提示 —— 那等于确认了后台的存在，
		// 对非管理员没有意义，直接让他回到熟悉的地方更干净。
		if (!session || !isAdmin) {
			return <div className={classes.loading}>
				<div style={{textAlign: 'center'}}>
					<div style={{marginBottom: 16}}>{session ? '你没有管理权限' : '请先登录'}</div>
					<button className={`${classes.tb} ${classes.tbPrimary}`}
						onClick={() => navigateHash(session ? '#library' : '#login')}>
						{session ? '返回编曲练习库' : '去登录'}
					</button>
				</div>
			</div>
		}
		return <AdminPage
			userName={displayName(session)}
			onHome={() => navigateHash('')}
			onLogout={handleLogout}
		/>
	}

	if (route.page == 'login') {
		// 登录成功回首页（已登录时 AuthPage 自己会立刻 onDone）
		return <AuthPage onDone={() => navigateHash('')} onHome={() => navigateHash('')} onSkip={() => navigateHash('#edit')} />
	}

	if (route.page == 'library') {
		if (!session) {
			// 送登录页，由上面那个 effect 完成跳转；这一帧先占位，别闪出编曲练习库的空壳
			return <div className={classes.loading}>加载中…</div>
		}
		return <LibraryPage
			user={session}
			onOpenScore={id => navigateHash('#edit/' + id)}
			onHome={() => navigateHash('')}
			onLogout={handleLogout}
			isAdmin={isAdmin}
			onAdmin={() => navigateHash('#admin')}
			onProfile={() => navigateHash('#profile')}
		/>
	}

	if (route.page == 'profile') {
		// 未登录不给看设置页，送回登录（与编曲练习库未登录同一处理）
		if (!session) {
			return <div className={classes.loading}>加载中…</div>
		}
		return <ProfilePage
			user={session}
			onHome={() => navigateHash('#library')}
			onLogout={handleLogout}
		/>
	}

	return <EditorPage key={route.scoreId ?? 'local'} session={session} scoreId={route.scoreId} onLogout={handleLogout} />
}
